import { useEffect, useState, useRef, useCallback, useMemo, useDeferredValue } from "react";
import AgGridTable from "../../components/table/AgGridTable";
import { Card, Drawer, Box, Typography, IconButton, useTheme } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
// Asset Info tab icons — one icon per SECTION badge only (no per-field icons —
// removed per feedback that they cluttered rows and squeezed value width).
// All section badges share one accent color (indigo, this app's own brand
// color) rather than a rainbow per section.
import SortIcon from "@mui/icons-material/Sort";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import PublicIcon from "@mui/icons-material/Public";
import MemoryIcon from "@mui/icons-material/Memory";
import ShieldIcon from "@mui/icons-material/Shield";
import RouterIcon from "@mui/icons-material/Router";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ComputerIcon from "@mui/icons-material/Computer";
import LaptopIcon from "@mui/icons-material/Laptop";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import PrintIcon from "@mui/icons-material/Print";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import DnsIcon from "@mui/icons-material/Dns";
import VideocamIcon from "@mui/icons-material/Videocam";
import SecurityIcon from "@mui/icons-material/Security";
import PhoneIcon from "@mui/icons-material/Phone";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import SensorsIcon from "@mui/icons-material/Sensors";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared/endpoints";
import { toast } from "react-toastify";
import moment from "moment";
import WhiteTooltip from "../../components/WhiteTooltip";
import SkeletonLoader from "../../modules/dashboard/shared/skeleton_loader";
import { Tabs, Tab, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, Button, Popover, Stack, RadioGroup, Radio, FormControlLabel, Divider, Chip, TableContainer, Table, TableBody, TableRow, TableCell, Paper, Checkbox, Autocomplete, TextField } from "@mui/material";
import { Settings } from "@mui/icons-material";
import { useSearchParams, useLocation } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DiscoveryRescanPopup from "./DiscoveryRescanPopup";
import eventEmitter from "../../components/utils/eventEmitter";
import Software from "../Software/Software";
import AIAgentTab from "../Software/AIAgentTab";


// Pagination constants
const PAGE_SIZE = 50;

// Utility to convert snake_case or camelCase to Title Case
const toTitleCase = (str: string) =>
  str
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/\s+/g, " ")
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();


interface DiscoveryProps {
  scanId?: string;
  fitDrawer?: boolean;
}

const ActionCellRenderer = (props: any) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: React.MouseEvent<HTMLElement>) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const handleRescan = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleClose();
    if (props.onRescan) {
      props.onRescan(props.data);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <IconButton size="small" onClick={handleClick}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleRescan} sx={{ fontSize: "14px" }}>Rescan</MenuItem>
      </Menu>
    </Box>

  );
};

// Formats a raw byte count (e.g. memory/disk size) into a short human string.
// Returns the original string unchanged if it isn't a plain numeric byte count.
const formatBytes = (value: string): string => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0 || !/^\d+$/.test(value.trim())) return value;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let size = n;
  let unitIdx = 0;
  while (size >= 1024 && unitIdx < units.length - 1) {
    size /= 1024;
    unitIdx += 1;
  }
  return `${size % 1 === 0 ? size : size.toFixed(1)} ${units[unitIdx]}`;
};

// Formats a raw ISO timestamp (e.g. "2026-07-02T17:05:45.967976Z") into the
// same human-readable format already used for the grid's Timestamp column —
// falls back to the original string if it isn't a parseable date.
const formatDateTime = (value: string): string => {
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD-MMM-YYYY, HH:mm:ss") : value;
};

// Buckets a (lowercased) device type into the same groups as the stat
// cards and the Device Type chip colors — shared so the top stat cards can
// be recomputed against a filtered subset without duplicating this list.
type StatBucket = "servers" | "workstations" | "networkDevices" | "iotDevices" | null;
const categorizeDeviceType = (deviceTypeLower: string): StatBucket => {
  if (deviceTypeLower.includes("server")) return "servers";
  if (deviceTypeLower.includes("workstation") || deviceTypeLower.includes("desktop") || deviceTypeLower.includes("laptop")) return "workstations";
  if (deviceTypeLower.includes("router") || deviceTypeLower.includes("switch") || deviceTypeLower.includes("firewall") || deviceTypeLower.includes("access point") || deviceTypeLower.includes("network")) return "networkDevices";
  if (deviceTypeLower.includes("camera") || deviceTypeLower.includes("cctv") || deviceTypeLower.includes("webcam") || deviceTypeLower.includes("printer") || deviceTypeLower.includes("biometric") || deviceTypeLower.includes("ip phone") || deviceTypeLower.includes("voip") || deviceTypeLower.includes("mobile") || deviceTypeLower.includes("phone")) return "iotDevices";
  return null;
};

const isFacetActive = (selectedList: string[], value: string) => {
  if (!selectedList || selectedList.length === 0) return true;
  if (selectedList.includes("__NONE__")) return false;
  return selectedList.includes(value);
};

// Shortens long opaque identifiers (agent UUIDs) to head…tail for display —
// the full value stays available via the copy button and hover tooltip.
const truncateMiddle = (value: string, head = 8, tail = 6): string => {
  if (value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
};

// Status words get a small colored dot next to the value (enabled/disabled,
// active/inactive) — a lightweight, real signal, not decoration.
const STATUS_DOT_COLORS: Record<string, string> = {
  enabled: "#00A61B",
  active: "#00A61B",
  running: "#00A61B",
  disabled: "#B71E31",
  inactive: "#8892A0",
  stopped: "#B71E31",
};

// Canonical severity colors — reused for the hostname criticality bar, the
// grid's Severity column, and Risk Score pill, so there's one scheme instead
// of inventing new colors per usage.
const SEVERITY_COLORS: Record<string, string> = {
  critical: "#B71E31",
  high: "#FF6600",
  medium: "#E0B102",
  low: "#00A61B",
};

// Device-type chip styling for the grid's Device Type column — same category
// grouping as the Asset Info hero icon, but colors instead of a single icon
// color, so each type reads distinctly in a table full of rows.
const DEVICE_TYPE_CHIP_STYLES: Array<{ match: (t: string) => boolean; icon: React.ElementType; color: string }> = [
  { match: (t) => t.includes("camera") || t.includes("cctv") || t.includes("webcam"), icon: VideocamIcon, color: "#10B981" },
  { match: (t) => t.includes("printer"), icon: PrintIcon, color: "#14B8A6" },
  { match: (t) => t.includes("firewall"), icon: SecurityIcon, color: "#F04438" },
  { match: (t) => t.includes("biometric"), icon: FingerprintIcon, color: "#F59E0B" },
  { match: (t) => t.includes("ip phone") || t.includes("voip"), icon: PhoneIcon, color: "#06B6D4" },
  { match: (t) => t.includes("mobile") || t.includes("phone"), icon: PhoneAndroidIcon, color: "#06B6D4" },
  { match: (t) => t.includes("server"), icon: DnsIcon, color: "#6366F1" },
  { match: (t) => t.includes("laptop"), icon: LaptopIcon, color: "#3B82F6" },
  { match: (t) => t.includes("workstation") || t.includes("desktop") || t.includes("endpoint"), icon: ComputerIcon, color: "#3B82F6" },
  { match: (t) => t.includes("router") || t.includes("switch") || t.includes("network") || t.includes("access point"), icon: RouterIcon, color: "#8B5CF6" },
];

const getDeviceTypeChipStyle = (deviceType: string | null | undefined): { icon: React.ElementType; color: string } => {
  const t = String(deviceType || "").toLowerCase();
  const match = DEVICE_TYPE_CHIP_STYLES.find((entry) => entry.match(t));
  return match ? { icon: match.icon, color: match.color } : { icon: HelpOutlineIcon, color: "#6B7280" };
};

// Small copy-to-clipboard affordance for fields marked `copyable` in
// ASSET_INFO_SCHEMA (Serial Number, IP, MAC, Agent ID) — its own component so
// each instance can hold its own "just copied" state.
const CopyIconButton: React.FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // clipboard API unavailable — nothing to fall back to
    }
  };
  return (
    <WhiteTooltip title={copied ? "Copied!" : "Copy"}>
      <IconButton size="small" onClick={handleCopy} sx={{ p: 0.4, flexShrink: 0 }}>
        {copied ? <CheckIcon sx={{ fontSize: 14, color: "success.main" }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
      </IconButton>
    </WhiteTooltip>
  );
};

const Discovery: React.FC<DiscoveryProps> = ({ scanId: propScanId, fitDrawer = false }) => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const discoveryScanId = propScanId || searchParams.get("scan_id") || "";
  const [scanStatusInfo, setScanStatusInfo] = useState<{
    status?: string;
    assetsDiscovered?: number;
    assetsAssessed?: number;
  } | null>(() => {
    const state = location.state as any;
    if (state && 'scanStatus' in state) {
      return {
        status: state.scanStatus,
        assetsDiscovered: state.assetsDiscovered,
        assetsAssessed: state.assetsAssessed,
      };
    }
    return null;
  });

  useEffect(() => {
    const fetchScanInfo = async () => {
      if (!discoveryScanId || scanStatusInfo) return;
      try {
        const response = await axiosInstance.post(END_POINTS.MY_SCAN_V2, {
          timezone: "Asia/Calcutta",
          filter_sched: [],
          search: "",
        });
        const data = response?.data?.data || response?.data || [];
        if (Array.isArray(data)) {
          const matchedScan = data.find(
            (scan: any) => String(scan.scan_uuid ?? scan.scan_id ?? scan.id) === String(discoveryScanId)
          );
          if (matchedScan) {
            const rawType = matchedScan.type || matchedScan.scan_type || "";
            const isDiscoveryType = String(rawType).toLowerCase().includes("discovery");
            
            const mapScanStatus = (status: unknown): string => {
              if (status === 1 || status === 2 || status === "1" || status === "2") {
                return "In Progress";
              }
              const statusByCode: Record<number, string> = {
                3: "Success",
                4: "Failed",
                5: "Cancelled",
                6: "Partially Success",
                7: "Fetching Configuration",
                8: "Fetching Configuration Success",
                9: "Fetching Configuration Failed",
              };
              if (typeof status === "number" && statusByCode[status]) return statusByCode[status];
              if (typeof status === "string" && status.trim()) {
                const normalized = status.trim().toLowerCase();
                const statusByLabel: Record<string, string> = {
                  queue: "Queue",
                  processing: "Processing",
                  completed: "Completed",
                  success: "Success",
                  failed: "Failed",
                  cancelled: "Cancelled",
                  "partially success": "Partially Success",
                  "in progress": "In Progress",
                  paused: "Paused",
                  pause: "Paused",
                };
                if (statusByLabel[normalized]) return statusByLabel[normalized];
              }
              return "In Progress";
            };

            setScanStatusInfo({
              status: mapScanStatus(matchedScan.status),
              assetsDiscovered: matchedScan.discovered_assets ?? matchedScan.assetsDiscovered ?? 0,
              assetsAssessed: isDiscoveryType
                ? (matchedScan.assessed_assets ?? matchedScan.assessedAssets ?? 0)
                : (matchedScan.passed_count ?? 0),
            });
          }
        }
      } catch (error) {
        console.error("Error fetching scan info in Discovery:", error);
      }
    };

    fetchScanInfo();
  }, [discoveryScanId, scanStatusInfo]);

  const [isLoading, setIsLoading] = useState(true);
  const [discoveryData, setDiscoveryData] = useState<any[]>([]);
  const [columnDefs, setColumnDefs] = useState<any[]>([]);
  const [assetStats, setAssetStats] = useState<{
    total: number;
    servers: number;
    workstations: number;
    networkDevices: number;
    iotDevices: number;
    /** AI agents (tools) found across all assets. */
    aiAgents: number;
  } | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<"all" | "servers" | "workstations" | "networkDevices" | "iotDevices" | "ai">("all");
  // AI Agents card: the panel listing each agent, and the agent the table is narrowed to (with the "ai" category).
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiToolFilter, setAiToolFilter] = useState<string | null>(null);
  const [quickFilters, setQuickFilters] = useState<{ deviceTypes: string[]; vendors: string[]; osNames: string[] }>({
    deviceTypes: [],
    vendors: [],
    osNames: [],
  });
  const [tempQuickFilters, setTempQuickFilters] = useState<{ deviceTypes: string[]; vendors: string[]; osNames: string[] }>({
    deviceTypes: [],
    vendors: [],
    osNames: [],
  });
  // The checkbox/chip UI reads `quickFilters` directly (must update
  // synchronously so ticking a box feels instant). The grid — which re-runs
  // custom cell renderers for every row and is the actually expensive part —
  // reads this deferred copy instead, so it can lag a frame or two behind
  // without blocking the checkbox's own visual feedback. (useTransition was
  // tried first but wraps the SAME state the checkbox reads, so it delayed
  // the checkbox too — useDeferredValue on a separate derived value is the
  // correct fix.)
  const deferredQuickFilters = useDeferredValue(quickFilters);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<HTMLElement | null>(null);
  const [activeFilterTab, setActiveFilterTab] = useState<"deviceTypes" | "vendors" | "osNames">("deviceTypes");
  const [filterSearchQuery, setFilterSearchQuery] = useState("");
  // One row per real asset's {deviceType, vendor, osName} triple (from the
  // full dataset, not just the current page) — lets the Device Type, Vendor,
  // and OS Name dropdowns cross-filter each other (e.g. picking "Workstation"
  // narrows Vendor and OS Name to only values that actually have one).
  const [assetFacetRows, setAssetFacetRows] = useState<Array<{ deviceType: string; vendor: string; osName: string; aiCount: number; aiTools: string[] }>>([]);
  // Full transformed rows for the same all-pages fetch above. Quick filters
  // must search this, not `discoveryData` (only the current server-paginated
  // page) — otherwise filtering to a device type that has zero matches on
  // the currently-loaded page reads as "0 results" even though matches
  // exist on another page.
  const [allAssetRows, setAllAssetRows] = useState<any[]>([]);
  const [portsOverlayOpen, setPortsOverlayOpen] = useState(false);
  const [osOverlayOpen, setOsOverlayOpen] = useState(false);
  const [selectedRowPorts, setSelectedRowPorts] = useState<any[]>([]);
  const [selectedRowOS, setSelectedRowOS] = useState<any[]>([]);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [ipDetailsData, setIpDetailsData] = useState<any>(null);
  const [ip, setIp] = useState<any>("");
  const [isLoadingIpDetails, setIsLoadingIpDetails] = useState(false);
  const [detailsTab, setDetailsTab] = useState<string>("assetInfo");
  // Tab the details drawer opens on; the AI Agents cell sets it to "aiAgent" before opening.
  const detailsTabOnOpenRef = useRef<string>("assetInfo");
  const [filteredCount, setFilteredCount] = useState<number | null>(null);

  useEffect(() => {
    if (detailsDrawerOpen) {
      setDetailsTab(detailsTabOnOpenRef.current);
      detailsTabOnOpenRef.current = "assetInfo";
    }
  }, [selectedRowData, detailsDrawerOpen]);

  const [rescanPopupOpen, setRescanPopupOpen] = useState(false);
  const [rescanRowData, setRescanRowData] = useState<any>(null);

  // Asset Criticality Popover state
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverRow, setPopoverRow] = useState<any>(null);
  const [popoverPosition, setPopoverPosition] = useState<any>(null);
  const [selectedCriticality, setSelectedCriticality] = useState<string>("");
  const [showRadioOptions, setShowRadioOptions] = useState<boolean>(false);
  const [assetCriticalityOptions, setAssetCriticalityOptions] = useState<any[]>([]);
  const [isUpdatingCriticality, setIsUpdatingCriticality] = useState(false);
  // Pagination state for server-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState(""); // Server-side search keyword

  const gridRef = useRef<any>(null);

  // Listen for socket events to update criticality locally for myscans
  useEffect(() => {
    const handleCriticalityUpdate = (response: any) => {
      // Check if it's a myscan event and we are currently viewing a myscan
      if (response && response.scan_type === "myscan" && discoveryScanId && Array.isArray(response.criticality_updates)) {
        setDiscoveryData((prevData: any) => {
          if (!prevData || !Array.isArray(prevData)) return prevData;
          let updated = false;
          const newData = prevData.map((item: any) => {
            const update = response.criticality_updates.find((u: any) => u.device_id === item.device_id || u.device_id === item.id);
            if (update) {
              updated = true;
              // Return updated item. Make sure to map criticality level correctly if needed
              // The backend sends criticality as a string like "Critical" or ID. 
              // Assuming 'criticality' is stored as the string label or ID.
              // In the payload it says: criticality: "Critical"
              return { ...item, criticality: update.criticality.toLowerCase() };
            }
            return item;
          });
          return updated ? newData : prevData;
        });
      }
    };

    eventEmitter.on('asset_criticality_update', handleCriticalityUpdate);
    return () => {
      eventEmitter.off('asset_criticality_update', handleCriticalityUpdate);
    };
  }, [discoveryScanId]);

  const getAssetCriticality = async () => {
    try {
      const response = await axiosInstance.get(END_POINTS.ASSETS_CRITICALITY);
      if (response?.status === 200) {
        setAssetCriticalityOptions(response?.data?.data?.dropdown);
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Something went wrong",
        { type: "error" }
      );
    }
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement> | null, data: any, e?: React.MouseEvent<HTMLElement>) => {
    // Accommodate both direct click (with event) and action menu (where anchorEl is managed differently)
    const target = event ? event.currentTarget : (e ? e.currentTarget : document.body);
    const clientY = event ? event.clientY : (e ? e.clientY : 0);
    const clientX = event ? event.clientX : (e ? e.clientX : 0);

    getAssetCriticality();
    setPopoverAnchor(target);
    setPopoverRow(data);
    setPopoverPosition({ top: clientY, left: clientX });
    setSelectedCriticality(data?.criticality || "");
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setPopoverRow(null);
    setSelectedCriticality("");
    setShowRadioOptions(false);
  };

  const handleApply = () => {
    const selectedCriticalityId = assetCriticalityOptions.find(
      (o: any) => o.label === selectedCriticality
    )?.id;
    if (popoverRow && selectedCriticalityId !== undefined) {
      handleAssetCriticality(popoverRow, selectedCriticalityId);
    }
  };

  const handleAssetCriticality = async (row: any, criticalityId: any) => {
    if (isUpdatingCriticality) return;
    try {
      setIsUpdatingCriticality(true);
      console.log("Full Row Data:", row);
      const payload = {
        device_id: [row.device_id || row.id],
        type: discoveryScanId ? "myscan" : "discovery",
        criticality: criticalityId,
      };
      
      console.log("Sending Asset Criticality Payload:", payload);
      
      const response = await axiosInstance.post(
        END_POINTS.ASSETS_CRITICALITY_UPDATE,
        payload
      );
      if (response?.status === 200) {
        toast.success("Asset criticality updated successfully");
        handlePopoverClose();
        // Refresh the page data
        fetchSecurityScanData(currentPage, searchKeyword);
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Something went wrong",
        { type: "error" }
      );
    } finally {
      setIsUpdatingCriticality(false);
    }
  };

  const getLabelText = (value: any) => {
    const option = assetCriticalityOptions.find(
      (o: any) => o.label === value?.criticality
    );
    if (option?.label) return option.label;
    if (value?.criticality) {
      return value.criticality.charAt(0).toUpperCase() + value.criticality.slice(1).toLowerCase();
    }
    return "Unknown";
  };

  const criticalityColorsForPopover: any = {
    critical: "#B71E31",
    high: "#FF6600",
    medium: "#E0B102",
    low: "#00A61B",
    default: "#B71E31",
  };

  const popoverContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: "10px 13px",
        gap: 2,
        boxShadow: 3,
        borderRadius: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography
          sx={{
            fontFamily: "Manrope, Helvetica",
            fontWeight: 700,
            fontSize: "0.75rem",
            lineHeight: "20px",
          }}
        >
          {popoverRow?.hostname || popoverRow?.ip || "Unknown"} :{" "}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "#f3f3f3",
            px: 1,
            py: 0.5,
            borderRadius: "6px",
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor:
                criticalityColorsForPopover[popoverRow?.criticality?.toLowerCase()] ||
                "goldenrod",
              mr: 0.5,
            }}
          />
          <Typography
            sx={{
              fontFamily: "Geologica, Helvetica, sans-serif",
              fontWeight: 600,
              color:
                criticalityColorsForPopover[popoverRow?.criticality?.toLowerCase()] ||
                "goldenrod",
              fontSize: "12px",
            }}
          >
            {getLabelText(popoverRow)}
          </Typography>
        </Box>
      </Stack>
      {!discoveryScanId && (
        <>
          <Divider sx={{ width: "100%" }} />
          <Stack spacing={2}>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              onClick={() => {
                setShowRadioOptions(!showRadioOptions);
              }}
              sx={{ cursor: "pointer" }}
            >
              <Settings sx={{ width: 16, height: 16 }} />
              <Typography
                sx={{
                  fontFamily: "Manrope, Helvetica",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                }}
              >
                Set Asset Criticality
              </Typography>
            </Stack>
            {showRadioOptions && (
              <RadioGroup
                value={selectedCriticality}
                onChange={(e) => setSelectedCriticality(e.target.value)}
              >
                {assetCriticalityOptions.map((option: any) => (
                  <FormControlLabel
                    key={option.label}
                    value={option.label}
                    control={<Radio size="small" />}
                    label={
                      <Typography
                        sx={{
                          fontFamily: "Manrope, Helvetica",
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          opacity: 0.8,
                        }}
                      >
                        {option.label}
                      </Typography>
                    }
                    sx={{ marginBottom: 0.5 }}
                  />
                ))}
              </RadioGroup>
            )}
            {showRadioOptions && (
              <Button
                onClick={() => handleApply()}
                disabled={selectedCriticality === "" || isUpdatingCriticality}
                variant="contained"
                sx={{
                  height: "21px",
                  textTransform: "none",
                  fontSize: "10px",
                  fontFamily: "Roboto, Helvetica",
                  borderRadius: "5px",
                  bgcolor: "#4338ca",
                  "&:hover": {
                    bgcolor: "#3730a3",
                  },
                }}
              >
                Apply
              </Button>
            )}
          </Stack>
        </>
      )}
    </Box>
  );

  const fetchIpDetails = async (_ipAddress: string, module: string, tool: string) => {
    setIsLoadingIpDetails(true);
    try {
      // const data = {
      //   name:"hi"
      // }
      setIp(_ipAddress);
      if (!module || module === "null" || module === "undefined" || module === "") {
        module = tool;
      }
      const response = await axiosInstance.get(`${END_POINTS.DISCOVERY_DETAILS_IP}?ip=${_ipAddress}`);
      if (response.status === 200) {

        const data = response?.data?.data || response?.data || null;
        setIpDetailsData(data);

        setIpDetailsData(data);


        console.log("IP Details API Response:", response.data);
        console.log("IP Details Data:", data);
        //toast.success("IP details loaded successfully", { type: "success" });
      }
    } catch (error: any) {
      console.error("Error fetching IP details:", error.response);
      console.error("Error details:", error);
      // toast.error(
      //   error.response?.data?.message || "Failed to fetch IP details",
      //   { type: "error" }
      // );
      setIpDetailsData(null);
    } finally {
      setIsLoadingIpDetails(false);
    }
  };

  // Helper: Normalize OS data into array of { name, accuracy, family, vendor }
  const normalizeOs = (osData: any): any[] => {
    const os: any[] = [];

    if (!osData) return os;

    // Case 1: OS is a simple string
    if (typeof osData === "string") {
      return [{ name: osData, accuracy: null, family: null, vendor: null }];
    }

    // Case 2: Already array of objects
    if (Array.isArray(osData)) {
      return osData.map((o) => ({
        name: o?.name || null,
        accuracy: o?.accuracy || null,
        family: o?.family || null,
        vendor: o?.vendor || null,
      }));
    }

    // Case 3: Object with possible array fields → zip them
    if (osData.name || osData.family || osData.vendor || osData.accuracy) {
      if (Array.isArray(osData.name)) {
        const len = Math.max(
          osData.name?.length || 0,
          osData.family?.length || 0,
          osData.vendor?.length || 0,
          osData.accuracy?.length || 0
        );

        for (let i = 0; i < len; i++) {
          os.push({
            name: osData.name?.[i] || null,
            accuracy: osData.accuracy?.[i] || null,
            family: osData.family?.[i] || null,
            vendor: osData.vendor?.[i] || null,
          });
        }
      } else {
        os.push({
          name: osData.name || null,
          accuracy: osData.accuracy || null,
          family: osData.family || null,
          vendor: osData.vendor || null,
        });
      }
    }

    return os;
  };

  // NEW: Transform discovery data from the new API format { devices: [...] }
  const transformDiscoveryData = (rawData: any) => {
    const result: any[] = [];

    // Handle both old format (array) and new format ({ devices: [...] })
    const devices = Array.isArray(rawData) ? null : rawData?.devices;

    // If new format with devices array
    if (devices && Array.isArray(devices)) {
      devices.forEach((device: any) => {
        const ip = device.ip || null;
        const hostname = device.hostname || device.fqdn || null;
        const deviceType = device.device_type || null;
        const vendor = device.vendor || null;
        const osName = device.os_name || null;
        const osFamily = device.os_family || null;

        // Extract ports directly from device (new API structure) and filter to only show open ports
        const ports = (Array.isArray(device.ports) ? device.ports : []).filter(
          (p: any) => p && p.state && p.state.toLowerCase() === "open"
        );

        // Support new API timestamp field, then fall back to legacy fields.
        const timestamp = device.timestamp || device.last_seen || device.first_seen || null;

        // Tool information
        const tool = device.last_tool || "nmap";
        const module = null; // Not provided in new API format

        // Create OS array for backward compatibility (if needed for OS overlay)
        const os: any[] = osName ? [{
          name: osName,
          family: osFamily,
          vendor: vendor,
          accuracy: null
        }] : [];

        // Scan metadata
        const scanCount = device.scan_count || 0;
        const matchType = device.match_type || null;
        const confidence = device.confidence || null;
        const comments = device.comments || null;

        // Criticality level
        const criticality = device.criticality || null;

        // IDs & Agent
        const id = device.id || device.device_id || null;
        const device_id = device.device_id || device.id || null;
        const agentName = device.agent_name || device.agent?.name || null;
        const agentId = device.agent_id || device.agent?.id || null;

        // Calculate ports count
        const ports_count = ports.length;

        // AI agents the endpoint agent found on this host. null = no endpoint data for it
        // (or an older API), which is not the same as 0.
        const aiInfo = device.ai_agent && typeof device.ai_agent === "object" ? device.ai_agent : null;
        const ai_tools: string[] = Array.isArray(aiInfo?.tools) ? aiInfo.tools.filter(Boolean).map(String) : [];
        const ai_agents_count: number | null = aiInfo
          ? (Number(aiInfo.software_count) > 0 || Number(aiInfo.signal_count) > 0 ? Math.max(ai_tools.length, 1) : 0)
          : null;

        // Calculate os_count
        const os_count = os.length;

        result.push({
          id,
          device_id,
          ip,
          hostname,
          deviceType,
          timestamp,
          ports,
          ports_count,
          ai_agents_count,
          ai_tools,
          module,
          tool,
          os,
          os_count,
          osName,
          vendor,
          osFamily,
          scanCount,
          matchType,
          confidence,
          criticality,
          agentName,
          agentId,
          toolsAvailable: [], // No tools breakdown in new API
          osFromOtherTools: false, // Set to false to show OS count when available
          comments
        });
      });

      return result;
    }

    // Fallback: Old format - loop over tool-based array
    if (Array.isArray(rawData)) {
      rawData.forEach((item) => {
        const tool = item.tool || null;

        if (Array.isArray(item.documents)) {
          item.documents.forEach((doc: any) => {
            const d = doc.data || {};

            const timestamp = d["@timestamp"] || null;

            const ip =
              d.scan_target?.ip ||
              (Array.isArray(d.host?.ip) ? d.host.ip[0] : d.host?.ip) ||
              d.host?.network?.ip ||
              null;

            const hostname = d.scan_target?.hostname || null;

            const deviceType = d.scan_target?.device_type || null;

            const ports = (Array.isArray(d.scan_target?.ports)
              ? d.scan_target?.ports
              : Array.isArray(d.scan_target?.network?.ports)
                ? d.scan_target?.network.ports
                : []).filter(
                  (p: any) => p && p.state && p.state.toLowerCase() === "open"
                );

            let os: any[] = [];
            if (d.scan_target?.os) os = os.concat(normalizeOs(d.scan_target?.os));

            const module = d.event?.module || null;

            // Get OS name with priority: WMI → SSH → NMAP
            let osName = null;

            // 1. Try WMI first
            osName = d.host?.os?.name || null;

            // 2. Try SSH if WMI doesn't have it
            if (!osName) {
              osName = d.device_info?.os_firmware_version || null;
            }

            // 3. Try NMAP if SSH doesn't have it - pick best accuracy OS name
            if (!osName && os.length > 0) {
              const sorted = [...os].sort((a, b) => Number(b.accuracy || 0) - Number(a.accuracy || 0));
              osName = sorted[0]?.name || null;
            }

            // Calculate counts
            const ports_count = ports.length;
            const os_count = os.length;
            // Check if OS came from WMI/SSH (osFromOtherTools)
            const osFromOtherTools = Boolean(osName && (d.host?.os?.name || d.device_info?.os_firmware_version));
            const comments = d.comments || null;
            
            const id = d.scan_target?.id || d.host?.id || null;
            const device_id = d.scan_target?.device_id || d.host?.device_id || id;
            const agentName = d.agent?.name || d.agent_name || null;
            const agentId = d.agent?.id || d.agent_id || null;

            result.push({
              id,
              device_id,
              ip,
              hostname,
              deviceType,
              timestamp,
              ports,
              ports_count,
              module,
              tool,
              os,
              os_count,
              osName,
              agentName,
              agentId,
              osFromOtherTools,
              comments
            });
          });
        }
      });
    }

    return result;
  };

  // Fetch data with pagination support
  const fetchSecurityScanData = useCallback(
    async (page: number = 1, search: string = "") => {
      setIsLoading(true);
      setSearchKeyword(search); // Track current search keyword

      try {
        const response = await axiosInstance.get(
          discoveryScanId ? END_POINTS.DISCOVERY_SCAN_HISTORY : END_POINTS.SECURITYSCAN_DATA,
          {
            params: discoveryScanId
              ? {
                scan_id: discoveryScanId,
                page,
                page_size: PAGE_SIZE,
                search: search,
              }
              : {
                page,
                page_size: PAGE_SIZE,
                search: search,
              },
          }
        );

        if (response.status === 200) {
          // Handle both new format { devices: [...] } and old format { data: [...] }
          const rawData = response?.data?.devices ? response.data : (response?.data?.data || response?.data || []);
          const transformedResults = transformDiscoveryData(rawData);

          // Update pagination state from API response
          const pageInfo = response?.data?.pagination;
          if (pageInfo) {
            // Support both API formats: "page" or "current_page", "total" or "total_matches"
            setCurrentPage(pageInfo.page || pageInfo.current_page || page);
            setTotalRows(pageInfo.total || pageInfo.total_matches || 0);
            setTotalPages(pageInfo.total_pages || 1);
            setHasNextPage(pageInfo.has_next || pageInfo.hasNext || false);
            setHasPreviousPage(pageInfo.has_previous || pageInfo.hasPrevious || false);
            console.log("📊 Pagination info:", pageInfo);
          } else if (discoveryScanId) {
            setCurrentPage(1);
            setTotalRows(transformedResults.length);
            setTotalPages(1);
            setHasNextPage(false);
            setHasPreviousPage(false);
          }


          const data = transformedResults;

          console.log("Security Scan Data API Response:", response.data);
          console.log("Security Scan Data:", data);

          if (Array.isArray(data) && data.length > 0) {
            // Process the data to format timestamps and add counts for ports and OS
            const processedData = data.map((item: any) => {
              const newItem: any = { ...item };

              // Format timestamp fields (any field that looks like a date/time)
              Object.entries(newItem).forEach(([key, value]) => {
                if (
                  typeof value === "string" &&
                  (key.toLowerCase().includes("time") ||
                    key.toLowerCase().includes("date") ||
                    key === "timestamp")
                ) {
                  try {
                    newItem[key] = moment(value).format("DD-MMM-YYYY, HH:mm:ss");
                  } catch (e) {
                    // If moment parsing fails, keep original value
                    console.warn(`Could not parse timestamp for field ${key}:`, value);
                  }
                }
              });

              // Ensure counts are set (in case transformation didn't add them)
              if (!newItem.ports_count) {
                newItem.ports_count = Array.isArray(newItem.ports) ? newItem.ports.length : 0;
              }
              if (!newItem.os_count) {
                const osArrayCount = Array.isArray(newItem.os) ? newItem.os.length : 0;
                newItem.os_count = newItem.osFromOtherTools && newItem.osName && osArrayCount === 0 ? 1 : osArrayCount;
              }

              return newItem;
            });

            // Set data (replace, not append - server-side pagination)
            console.log("✅ Setting page data:", processedData.length, "items for page", page);
            setDiscoveryData(processedData);

            // Generate dynamic column definitions from the first data item (only on first page)
            if (page === 1) {
              const firstObj = processedData[0];
              if (firstObj) {
                // Define the preferred column order
                const columnOrder = ['hostname', 'vendor', 'osName', 'ip', 'deviceType', 'ports_count', 'ai_agents_count', 'timestamp','comments'];

                const filteredKeys = Object.keys(firstObj)
                  .filter(key =>
                    key !=='id'&&
                    key !=='device_id' &&
                    key !== 'ports' &&
                    key !== 'os' &&
                    key !== 'tool' &&
                    key !== 'module' &&
                    key !== 'os_count' &&
                    key !== 'ai_tools' &&
                    key !== 'toolsAvailable' &&
                    key !== 'osFromOtherTools' &&
                    key !== 'osFamily' &&
                    key !== 'scanCount' &&
                    key !== 'matchType' &&
                    key !== 'confidence' &&
                    key !== 'criticality' &&
                    key !== 'agentName' &&
                    key !== 'agent_name' &&
                    key !== 'agentId' &&
                    key !== 'agent_id'
                  );

                // Sort keys based on preferred order, remaining keys go at the end
                const sortedKeys = filteredKeys.sort((a, b) => {
                  const aIndex = columnOrder.indexOf(a);
                  const bIndex = columnOrder.indexOf(b);
                  // If both are in the preferred order, sort by their position
                  if (aIndex !== -1 && bIndex !== -1) {
                    return aIndex - bIndex;
                  }
                  // If only a is in the preferred order, a comes first
                  if (aIndex !== -1) return -1;
                  // If only b is in the preferred order, b comes first
                  if (bIndex !== -1) return 1;
                  // If neither is in the preferred order, maintain original order
                  return 0;
                });

                const dynamicColumnDefs = sortedKeys
                  .map((key) => {
                    let headerName = toTitleCase(key);
                    const isHostname = key === 'hostname';
                    const isTimestamp = key.toLowerCase().includes("time") ||
                      key.toLowerCase().includes("date") ||
                      key === "timestamp";
                    const isPortsCount = key === "ports_count";
                    const isAiAgents = key === "ai_agents_count";
                    const isOsName = key === "osName";
                    const isComments = key === "comments";

                    // Override header names for count columns
                    if (isPortsCount) {
                      headerName = "Ports";
                    } else if (isAiAgents) {
                      headerName = "AI Agents";
                    } else if (isOsName) {
                      headerName = "Os Name";
                    }

                    const baseColumnDef = {
                      headerName,
                      field: key,
                      minWidth: isTimestamp ? Math.max(180, headerName.length * 12) : isComments ? 90 : Math.max(150, headerName.length * 12),
                      maxWidth: isTimestamp ? 260 : isComments ? 130 : undefined,
                      width: isComments ? 110 : undefined,

                      // tooltipValueGetter: (params: any) =>
                      //   params.value !== undefined && params.value !== null
                      //     ? String(params.value)
                      //     : "",
                      sortable: true,
                      filter: true,
                      resizable: true,
                      valueFormatter: (params: any) => {
                        if (params.value === null || params.value === undefined || params.value === "") {
                          return "-";
                        }
                        return params.value;
                      }
                    };

                    // AI agents on this host: the count, with the tool names on hover. Clicking it opens
                    // the details drawer on the AI Agent tab when agents were found, like the other cells.
                    if (isAiAgents) {
                      return {
                        ...baseColumnDef,
                        minWidth: 120,
                        filter: false,
                        cellRenderer: (params: any) => {
                          const count = params.value;
                          const tools: string[] = Array.isArray(params.data?.ai_tools) ? params.data.ai_tools : [];
                          const hasCount = count !== null && count !== undefined;
                          const handleClick = () => {
                            detailsTabOnOpenRef.current = hasCount && count > 0 ? "aiAgent" : "assetInfo";
                            setSelectedRowData(params.data);
                            setDetailsDrawerOpen(true);
                            fetchIpDetails(params.data.ip, params.data.module, params.data.tool);
                          };
                          return (
                            <div
                              onClick={handleClick}
                              style={{
                                cursor: 'pointer',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              {hasCount ? (
                                <span title={count ? (tools.join(", ") || "AI agents detected") : "No AI agents found"} style={{ fontWeight: 600 }}>
                                  {count}
                                </span>
                              ) : (
                                <span title="No endpoint agent data for this host">-</span>
                              )}
                            </div>
                          );
                        },
                      };
                    }

                    // Add click functionality for ports count column
                    if (isPortsCount) {
                      return {
                        ...baseColumnDef,
                        cellRenderer: (params: any) => {
                          const count = params.value;
                          const handlePortsClick = (e: React.MouseEvent) => {
                            e.stopPropagation();
                            if (params.data.ports && params.data.ports.length > 0) {
                              setSelectedRowPorts(params.data.ports);
                              setPortsOverlayOpen(true);
                            }
                          };
                          const handleRowClick = () => {
                            setSelectedRowData(params.data);
                            setDetailsDrawerOpen(true);
                            fetchIpDetails(params.data.ip, params.data.module, params.data.tool);
                          };

                          if (count > 0) {
                            return (
                              <div
                                onClick={handleRowClick}
                                style={{
                                  cursor: 'pointer',
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <span
                                  style={{
                                    color: '#1976d2',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                  }}
                                  onClick={handlePortsClick}
                                >
                                  {count}
                                </span>
                              </div>
                            );
                          }
                          return (
                            <div
                              onClick={handleRowClick}
                              style={{
                                cursor: 'pointer',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              {count}
                            </div>
                          );
                        }
                      };
                    }
                    // OS Name column - show OS name with clickable count
                    else if (isOsName) {
                      return {
                        ...baseColumnDef,
                        minWidth: 220,
                        resizable: true,
                        cellRenderer: (params: any) => {
                          const osName = params.value;
                          const osCount = Number(params.data.os_count || 0);
                          const osFromOtherTools = params.data.osFromOtherTools;
                          const handleOsClick = (e: React.MouseEvent) => {
                            e.stopPropagation();
                            if (params.data.os && params.data.os.length > 0) {
                              setSelectedRowOS(params.data.os);
                              setOsOverlayOpen(true);
                            }
                          };
                          const handleRowClick = () => {
                            setSelectedRowData(params.data);
                            setDetailsDrawerOpen(true);
                            fetchIpDetails(params.data.ip, params.data.module, params.data.tool);
                          };

                          // Display full OS name - will wrap or truncate based on column width
                          const displayName = osName || "-";

                          return (
                            <div onClick={handleRowClick} style={{ display: 'flex', alignItems: 'center', height: '100%', cursor: 'pointer', width: '100%' }}>
                              {osName ? (
                                <WhiteTooltip title={osName} placement="bottom" PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [0, 2] } }] }}>
                                  <span
                                    style={{
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      flex: 1,
                                      minWidth: 0
                                    }}
                                  >
                                    {displayName}
                                  </span>
                                </WhiteTooltip>
                              ) : (
                                <span
                                  style={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    flex: 1,
                                    minWidth: 0
                                  }}
                                >
                                  {displayName}
                                </span>
                              )}
                              {/* Commented out OS count in brackets - only showing OS name */}
                              {/* {osCount > 0 && (!osFromOtherTools) && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontWeight: 500, marginLeft: 4, lineHeight: 1 }}>
                                  <span>(</span>
                                  <WhiteTooltip title={`${osCount} detected`} placement="bottom" PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [6, 0] } }] }}>
                                    <span
                                      style={{
                                        color: '#1976d2',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        fontWeight: '500'
                                      }}
                                      onClick={handleOsClick}
                                    >
                                      {osCount}
                                    </span>
                                  </WhiteTooltip>
                                  <span>)</span>
                                </span>
                              )} */}
                            </div>
                          );
                        }
                      };
                    } else if (key === "comments") {
                      return {
                        ...baseColumnDef,
                        minWidth: 250,
                        resizable: true,
                        cellRenderer: (params: any) => {
                          const comments = params.value;
                          const handleRowClick = () => {
                            setSelectedRowData(params.data);
                            setDetailsDrawerOpen(true);
                            fetchIpDetails(params.data.ip, params.data.module, params.data.tool);
                          };
                          const displayName = comments || "-";

                          return (
                            <div onClick={handleRowClick} style={{ display: 'flex', alignItems: 'center', height: '100%', cursor: 'pointer', width: '100%' }}>
                              {comments ? (
                                <WhiteTooltip title={comments} placement="bottom" PopperProps={{ modifiers: [{ name: 'offset', options: { offset: [0, 2] } }] }}>
                                  <span
                                    style={{
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      flex: 1,
                                      minWidth:0
                                    }}
                                  >
                                    {displayName}
                                  </span>
                                </WhiteTooltip>
                              ) : (
                                <span
                                  style={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    flex: 1,
                                    minWidth: 0
                                  }}
                                >
                                  {displayName}
                                </span>
                              )}
                            </div>
                          );
                        }
                      };
                    } else if (isHostname) {
                      // Hostname column with criticality color bar (matching Assets page)
                      return {
                        ...baseColumnDef,
                        cellRenderer: (params: any) => {
                          const handleClick = () => {
                            setSelectedRowData(params.data);
                            setDetailsDrawerOpen(true);
                            fetchIpDetails(params.data.ip, params.data.module, params.data.tool);
                          };
                          const criticality = params.data?.criticality;
                          const barColor = criticality
                            ? SEVERITY_COLORS[criticality.toLowerCase()] || '#B71E31'
                            : '#B71E31';
                          return (
                            <div
                              onClick={handleClick}
                              style={{
                                cursor: 'pointer',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                overflow: 'hidden'
                              }}
                            >
                              {criticality && (
                                <div
                                  className="ag-criticality-bar"
                                  onClick={(e) => {
                                    if (!discoveryScanId) {
                                      e.stopPropagation();
                                      handlePopoverOpen(e, params.data);
                                    }
                                  }}
                                  style={{
                                    width: '4px',
                                    height: '32px',
                                    backgroundColor: barColor,
                                    borderRadius: '2px',
                                    marginRight: '8px',
                                    flexShrink: 0,
                                    cursor: discoveryScanId ? 'default' : 'pointer'
                                  }}
                                />
                              )}
                              <span
                                style={{
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  fontSize: '14px',
                                  flexGrow: 1,
                                  minWidth: 0
                                }}
                              >
                                {params.value || '-'}
                              </span>
                            </div>
                          );
                        }
                      };
                    } else if (key === "deviceType") {
                      return {
                        ...baseColumnDef,
                        cellRenderer: (params: any) => {
                          const handleClick = () => {
                            setSelectedRowData(params.data);
                            setDetailsDrawerOpen(true);
                            fetchIpDetails(params.data.ip, params.data.module, params.data.tool);
                          };
                          return (
                            <div
                              onClick={handleClick}
                              style={{
                                cursor: 'pointer',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <span
                                style={{
                                  overflow: 'hidden',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  fontSize: '14px',
                                  textTransform: 'capitalize'
                                }}
                              >
                                {params.value || '-'}
                              </span>
                            </div>
                          );
                        }
                      };
                    } else {
                      return {
                        ...baseColumnDef,
                        cellRenderer: (params: any) => {
                          const handleClick = () => {
                            setSelectedRowData(params.data);
                            setDetailsDrawerOpen(true);
                            fetchIpDetails(params.data.ip, params.data.module, params.data.tool);
                          };
                          return (
                            <div
                              onClick={handleClick}
                              style={{
                                cursor: 'pointer',
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              {params.value}
                            </div>
                          );
                        }
                      };
                    }
                  });

                dynamicColumnDefs.push({
                  headerName: "Action",
                  field: "action",
                  minWidth: 85,
                  maxWidth: 100,
                  width: 90,
                  sortable: false,
                  filter: false,
                  suppressMenu: true,
                  menuTabs: [],
                  resizable: false,
                  cellRenderer: ActionCellRenderer,
                  cellRendererParams: {
                    onRescan: (data: any) => {
                      setRescanRowData(data);
                      setRescanPopupOpen(true);
                    }
                  }
                });

                setColumnDefs(dynamicColumnDefs);
                console.log("Generated Column Definitions:", dynamicColumnDefs);
              }
            }
          } else {
            setDiscoveryData([]);
            if (page === 1) {
              setColumnDefs([]);
            }
            console.log("No data received from API");
          }

          // toast.success("Security scan data loaded successfully", { type: "success" });
        }
      } catch (error: any) {
        console.error("Error fetching security scan data:", error.response);
        console.error("Error details:", error);
        toast.error(
          error.response?.data?.message || "Failed to fetch security scan data",
          { type: "error" }
        );
        setDiscoveryData([]);
        setColumnDefs([]);
      } finally {
        setIsLoading(false);
      }
    },
    [discoveryScanId]
  );

  // One extra lightweight fetch across the FULL result set (not just the
  // current page) to compute real stat-card counts. `discoveryData` only
  // ever holds PAGE_SIZE rows, so counting from it would misrepresent totals
  // once there's more than one page. Asking for page_size:100000 does NOT
  // get everything in one shot — the backend hard-caps page_size at 100
  // (see get_correlated_devices in spark-securityscan-v2) and silently
  // truncates, which is why this used to undercount (e.g. showing "100"
  // when the real total was 113). So this pages through in chunks of 100
  // instead, aggregating client-side rather than adding a new endpoint.
  const fetchAssetStats = useCallback(async () => {
    try {
      const STATS_PAGE_SIZE = 100;
      const MAX_PAGES = 50; // sanity cap (5,000 assets) so a bad total_pages value can't loop forever
      const rows: any[] = [];
      let page = 1;
      let totalPages = 1;

      do {
        const response = await axiosInstance.get(
          discoveryScanId ? END_POINTS.DISCOVERY_SCAN_HISTORY : END_POINTS.SECURITYSCAN_DATA,
          {
            params: discoveryScanId
              ? { scan_id: discoveryScanId, page, page_size: STATS_PAGE_SIZE }
              : { page, page_size: STATS_PAGE_SIZE },
          }
        );
        if (response.status !== 200) break;
        const rawData = response?.data?.devices ? response.data : (response?.data?.data || response?.data || []);
        rows.push(...transformDiscoveryData(rawData));
        totalPages = response?.data?.pagination?.total_pages || 1;
        page += 1;
      } while (page <= totalPages && page <= MAX_PAGES);

      let servers = 0;
      let workstations = 0;
      let networkDevices = 0;
      let iotDevices = 0;
      let aiAgents = 0;
      const facetRows: Array<{ deviceType: string; vendor: string; osName: string; aiCount: number; aiTools: string[] }> = [];

      for (const row of rows) {
        const dt = String(row.deviceType || "").toLowerCase();
        const bucket = categorizeDeviceType(dt);
        if (bucket === "servers") servers += 1;
        else if (bucket === "workstations") workstations += 1;
        else if (bucket === "networkDevices") networkDevices += 1;
        else if (bucket === "iotDevices") iotDevices += 1;

        facetRows.push({
          deviceType: dt || "unknown",
          vendor: String(row.vendor || "").trim(),
          osName: String(row.osName || "").trim() || "Unknown",
          aiCount: Number(row.ai_agents_count) || 0,
          aiTools: Array.isArray(row.ai_tools) ? row.ai_tools : [],
        });
        aiAgents += Number(row.ai_agents_count) || 0;
      }

      setAssetStats({ total: rows.length, servers, workstations, networkDevices, iotDevices, aiAgents });
      setAssetFacetRows(facetRows);
      setAllAssetRows(rows);
    } catch (error) {
      console.error("Error fetching asset stats:", error);
    }
  }, [discoveryScanId]);

  useEffect(() => {
    fetchAssetStats();
  }, [fetchAssetStats]);

  // Initial fetch
  useEffect(() => {
    fetchSecurityScanData(1);
  }, [fetchSecurityScanData]);

  // Handle page navigation
  const handleNextPage = useCallback(() => {
    if (hasNextPage && !isLoading) {
      const nextPage = currentPage + 1;
      console.log("📄 Going to next page:", nextPage);
      fetchSecurityScanData(nextPage, searchKeyword);
    }
  }, [hasNextPage, isLoading, currentPage, fetchSecurityScanData, searchKeyword]);

  const handlePreviousPage = useCallback(() => {
    if (hasPreviousPage && !isLoading) {
      const prevPage = currentPage - 1;
      console.log("📄 Going to previous page:", prevPage);
      fetchSecurityScanData(prevPage, searchKeyword);
    }
  }, [hasPreviousPage, isLoading, currentPage, fetchSecurityScanData, searchKeyword]);

  const handleFirstPage = useCallback(() => {
    if (currentPage !== 1 && !isLoading) {
      console.log("📄 Going to first page");
      fetchSecurityScanData(1, searchKeyword);
    }
  }, [currentPage, isLoading, fetchSecurityScanData, searchKeyword]);

  const handleLastPage = useCallback(() => {
    if (currentPage !== totalPages && !isLoading) {
      console.log("📄 Going to last page:", totalPages);
      fetchSecurityScanData(totalPages, searchKeyword);
    }
  }, [currentPage, totalPages, isLoading, fetchSecurityScanData, searchKeyword]);

  // Memoized grid options - no pagination handler needed since we use custom controls
  const memoizedGridOptions: any = useMemo(
    () => ({
      rowHeight: 50,
      domLayout: 'normal',
      // getRowId gives AG-Grid a stable identity per row across data updates,
      // which is what lets animateRows actually animate (fade/slide) rows
      // into their new positions on filter/sort instead of a hard redraw.
      animateRows: true,
      // Combine several fields rather than trusting a single one — `id` is
      // frequently null from the API and `ip` alone isn't guaranteed, so a
      // lone fallback risks duplicate row ids (AG-Grid then drops/merges
      // rows instead of animating them, which reads as rows disappearing).
      getRowId: (params: any) =>
        [params?.data?.id, params?.data?.device_id, params?.data?.ip, params?.data?.hostname]
          .filter(Boolean)
          .join("|") || JSON.stringify(params?.data),
    }),
    []
  );

  // ── Asset Info (curated, CrowdStrike-style summary) ─────────────────────────
  // Surfaces only the fields an analyst actually looks for first, grouped and
  // labeled — not a raw flattened dump of every field. A field is shown only
  // if a real value was found; absent data means the row is simply omitted.
  const ASSET_INFO_SCHEMA: Array<{
    title: string;
    icon: React.ElementType;
    fields: Array<{
      label: string;
      keys?: string[];
      value?: string;
      copyable?: boolean;
      format?: (v: string) => string;
      truncateDisplay?: boolean;
      monospace?: boolean;
    }>;
  }> = [
    {
      title: "Identification",
      icon: SortIcon,
      fields: [
        { label: "Hostname", keys: ["hostname"] },
        { label: "FQDN", keys: ["fqdn", "dns_computer"] },
        { label: "Device Type", keys: ["device_type", "devicetype"] },
        { label: "Vendor", keys: ["vendor", "manufacturer"] },
        { label: "Model", keys: ["model"] },
        { label: "Serial Number", keys: ["serial_number", "serialnumber"], copyable: true, monospace: true },
      ],
    },
    {
      // Network + Discovery merged into one card (fields interleaved:
      // network field, discovery field, ...) but titled just "Network" —
      // the Discovery fields (scan type, agent, last seen) still live here.
      title: "Network",
      icon: PublicIcon,
      fields: [
        { label: "IP Address", keys: ["ip"], copyable: true, monospace: true },
        { label: "Scan Type", value: "Passive Discovery" },
        { label: "MAC Address", keys: ["mac_address", "mac"], copyable: true, monospace: true },
        { label: "Agent Name", keys: ["agent_name", "agent.name", "agentname", "agent_id", "agent.id"], copyable: true },
        { label: "Domain", keys: ["domain"] },
        { label: "Last Seen", keys: ["last_seen", "timestamp"], format: formatDateTime },
        { label: "Open Ports", keys: ["ports_count"] },
      ],
    },
    {
      title: "Operating System",
      icon: DesktopWindowsIcon,
      fields: [
        { label: "OS Name", keys: ["os_name", "osname"] },
        { label: "OS Version", keys: ["os_version", "os_display_version"] },
        { label: "OS Build", keys: ["build_number"] },
        { label: "Architecture", keys: ["os_arch", "os_architecture", "architecture"] },
      ],
    },
    {
      title: "Hardware",
      icon: MemoryIcon,
      fields: [
        { label: "Processor", keys: ["processor.name"] },
        { label: "CPU Cores", keys: ["logical_count", "processor_count"] },
        { label: "Memory", keys: ["memory.size"], format: formatBytes },
        { label: "Disk", keys: ["disk.model"] },
        { label: "Disk Size", keys: ["disk.size"], format: formatBytes },
      ],
    },
    {
      title: "Security",
      icon: ShieldIcon,
      fields: [
        { label: "Antivirus", keys: ["antivirus.name"] },
        { label: "Antivirus State", keys: ["antivirus.state"] },
        { label: "Total CVEs", keys: ["cve.total_count", "total_count"] },
        { label: "Installed Software", keys: ["software.count"] },
      ],
    },
  ];

  // Deep-scans an object and indexes every leaf value under its last one and
  // last two path segments (lowercased). Handles two real shapes seen in the
  // search-ip response: genuinely nested objects (tools.nmap.data.agent.id)
  // AND flat keys that contain literal dots (tools.wmi.data["devices.hostname"]
  // is one property, not a nested "devices" object) — splitting each key on
  // "." before extending the path indexes both the same way, so a lookup for
  // "hostname" finds it either way.
  //
  // Different tools often report the same field at different levels of
  // detail (WMI: "Windows 11 Pro" vs. NMAP: "Windows 11") — whichever one
  // happens to be visited last would otherwise win arbitrarily. Instead,
  // when two sources both provide a value for the same key, keep the
  // longer string, since the more specific value is consistently the
  // longer one across the fields we show (OS name/version, vendor, etc.).
  const preferMoreSpecific = (existing: any, incoming: any) => {
    if (existing === undefined || existing === null || existing === "") return incoming;
    if (typeof existing === "string" && typeof incoming === "string") {
      return incoming.length > existing.length ? incoming : existing;
    }
    return existing;
  };

  const buildFieldLookup = (data: any, base?: Record<string, any>): Record<string, any> => {
    const lookup: Record<string, any> = { ...(base || {}) };
    const visit = (node: any, path: string[]) => {
      if (node === null || node === undefined || node === "") return;
      if (Array.isArray(node)) return; // ports/os arrays handled via explicit fields, not deep-indexed
      if (typeof node === "object") {
        Object.entries(node).forEach(([k, v]) => visit(v, [...path, ...k.split(".")]));
        return;
      }
      const last1 = path[path.length - 1]?.toLowerCase();
      const last2 = path.slice(-2).join(".").toLowerCase();
      if (last1) lookup[last1] = preferMoreSpecific(lookup[last1], node);
      if (last2) lookup[last2] = preferMoreSpecific(lookup[last2], node);
    };
    visit(data, []);
    return lookup;
  };

  // Shared by renderAssetInfo (section fields) and the drawer header's hero
  // summary (hostname/device type/severity) — both need the same merged
  // view of the normalized grid row layered under the deep WMI/SSH/SNMP
  // document, just different slices of it.
  const buildAssetLookup = (deepData: any, rowData: any): Record<string, any> => {
    const baseLookup: Record<string, any> = {};
    if (rowData) {
      if (rowData.ip) baseLookup.ip = rowData.ip;
      if (rowData.hostname) baseLookup.hostname = rowData.hostname;
      if (rowData.deviceType) baseLookup.device_type = rowData.deviceType;
      if (rowData.vendor) baseLookup.vendor = rowData.vendor;
      if (rowData.osName) baseLookup.os_name = rowData.osName;
      if (rowData.osFamily) baseLookup.os_family = rowData.osFamily;
      if (rowData.tool) baseLookup.tool = rowData.tool;
      if (rowData.criticality) baseLookup.criticality = rowData.criticality;
      if (rowData.timestamp) baseLookup.last_seen = rowData.timestamp;
      if (typeof rowData.ports_count === "number") baseLookup.ports_count = String(rowData.ports_count);
      if (rowData.agentName || rowData.agent_name) baseLookup.agent_name = rowData.agentName || rowData.agent_name;
      if (rowData.agentId || rowData.agent_id) baseLookup.agent_id = rowData.agentId || rowData.agent_id;
    }
    const lookup = buildFieldLookup(deepData, baseLookup);
    if (ip && !lookup.ip) lookup.ip = ip;
    return lookup;
  };

  // Hero summary (icon, hostname, device type, severity) shown in the
  // drawer's sticky header — persistent across all tabs, not just Asset Info.
  const computeHeroInfo = (deepData: any, rowData: any) => {
    const lookup = buildAssetLookup(deepData, rowData);
    const critValue: string | undefined = lookup.criticality;
    const critColor = critValue
      ? SEVERITY_COLORS[critValue.toLowerCase()] || theme.palette.text.secondary
      : SEVERITY_COLORS.low;
    const hostnameDisplay = lookup.hostname || ip || "Unknown Host";
    const { icon: HeroIcon } = getDeviceTypeChipStyle(lookup.device_type);
    return { lookup, critValue, critColor, hostnameDisplay, HeroIcon, deviceType: lookup.device_type as string | undefined };
  };

  // Helper to determine if an asset is Ubuntu, RedHat, or Debian (or Linux distros)
  // which do not support WMI Software / AI Agent tabs.
  const isExcludedLinuxDistro = (rowData: any, ipData: any): boolean => {
    const values: string[] = [];

    const add = (val: any) => {
      if (typeof val === "string" && val.trim()) {
        values.push(val.toLowerCase().trim());
      }
    };

    // From selectedRowData
    if (rowData) {
      add(rowData.osName);
      add(rowData.osFamily);
      add(rowData.vendor);
      add(rowData.deviceType);
      add(rowData.hostname);
      if (Array.isArray(rowData.os)) {
        rowData.os.forEach((o: any) => {
          add(o?.name);
          add(o?.family);
          add(o?.vendor);
        });
      }
    }

    // From ipData (deep response / search-ip payload)
    if (ipData) {
      const dev = ipData.device || ipData;
      const sshData = dev?.tools?.ssh?.data;
      const nmapData = dev?.tools?.nmap?.data;
      const scanTarget = nmapData?.scan_target;
      const devInfo = sshData?.api_response?.device_info;

      add(sshData?.device_type);
      add(sshData?.os_name);
      add(sshData?.device_type_used);
      add(sshData?.hostname);
      add(devInfo?.device_type);
      add(devInfo?.devices_os_name);
      add(devInfo?.devices_os_family);
      add(scanTarget?.os_name);
      add(scanTarget?.vendor);
      add(scanTarget?.os_hint);

      // Fallback: buildAssetLookup
      const lookup = buildAssetLookup(ipData, rowData);
      add(lookup.os_name);
      add(lookup.os_family);
      add(lookup.vendor);
      add(lookup.device_type);
      add(lookup.devices_os_name);
      add(lookup.devices_os_family);
    }

    const combined = values.join(" ");
    const excludedKeywords = [
      "ubuntu",
      "redhat",
      "red hat",
      "debian",
    ];

    return excludedKeywords.some((kw) => combined.includes(kw));
  };

  const renderFieldTable = (data: any) => {
    if (!data || typeof data !== "object") {
      return (
        <Typography variant="body2" color="text.secondary">No data</Typography>
      );
    }

    // If the payload is the cross_tool_ip_search response shape, extract documents[0].data
    const extractFirstDocumentData = (payload: any): any => {
      try {
        // Case 1: Array at root
        if (Array.isArray(payload) && payload.length > 0) {
          const first = payload[0];
          if (first && Array.isArray(first.documents) && first.documents[0]?.data) {
            return first.documents[0].data;
          }
        }
        // Case 2: Object with documents array
        if (!Array.isArray(payload) && Array.isArray(payload.documents) && payload.documents[0]?.data) {
          return payload.documents[0].data;
        }
      } catch (_) {
        // fall through and return payload
      }
      return payload;
    };

    const target = extractFirstDocumentData(data);

    // Some tools (notably software/app inventories) report an array or object
    // field as an already-JSON.stringify'd string instead of real nested JSON —
    // so Array.isArray/typeof object checks below never see it as structured
    // data, and it would otherwise render as one raw, doubly-escaped blob.
    // Parse it back into real data first so it gets the same per-entry
    // flattening as everything else.
    const tryParseJson = (v: string): any => {
      const trimmed = v.trim();
      if (!trimmed || (trimmed[0] !== "[" && trimmed[0] !== "{")) return undefined;
      try {
        return JSON.parse(trimmed);
      } catch {
        return undefined;
      }
    };

    const flatten = (obj: any, prefix = ""): Array<{ key: string; value: any }> => {
      const rows: Array<{ key: string; value: any }> = [];
      Object.entries(obj).forEach(([k, rawV]) => {
        const path = prefix ? `${prefix}.${k}` : k;
        const v = typeof rawV === "string" ? tryParseJson(rawV) ?? rawV : rawV;
        if (Array.isArray(v)) {
          // Arrays of objects (ports, os candidates, etc.) get flattened per-entry
          // under an indexed path — same as nested objects — instead of being
          // JSON.stringify'd whole into one cell, which used to produce a single
          // massive, unreadable row for any field with more than a couple entries.
          if (v.length > 0 && v.every((item) => item !== null && typeof item === "object")) {
            v.forEach((item, idx) => rows.push(...flatten(item, `${path}[${idx}]`)));
          } else {
            // Array of primitives (or empty) — still a single row, just joined
            // into a readable list rather than raw JSON.
            rows.push({ key: path, value: v.length > 0 ? v.join(", ") : "-" });
          }
        } else if (v !== null && typeof v === "object") {
          rows.push(...flatten(v, path));
        } else {
          rows.push({ key: path, value: v });
        }
      });
      return rows;
    };

    const rows = flatten(target);

    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxWidth: "100%" }}>
        <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell sx={{ width: "40%", fontWeight: 600, wordBreak: "break-word" }}>{row.key}</TableCell>
                <TableCell sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>{
                  row.value === undefined || row.value === null || row.value === "" ? "-" : String(row.value)
                }</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderAssetInfo = (deepData: any, rowData: any) => {
    // Normalized grid-row fields are a reliable base layer (criticality, tool,
    // ports_count, etc. live only here, not in the raw ES document); the deep
    // WMI/SSH/SNMP document is layered on top since it's more detailed/authoritative
    // for anything it actually has (real hostname, serial number, agent identity).
    const lookup = buildAssetLookup(deepData, rowData);

    const sections = ASSET_INFO_SCHEMA.map(({ title, icon, fields }) => {
      const items = fields
        .map(({ label, keys, value: staticValue, copyable, format, truncateDisplay, monospace }) => {
          if (staticValue !== undefined) {
            return { label, value: staticValue, copyable: Boolean(copyable), truncateDisplay: Boolean(truncateDisplay), monospace: Boolean(monospace) };
          }
          const raw = (keys || []).map((k) => lookup[k]).find((v) => v !== undefined && v !== null && v !== "");
          if (raw === undefined) return null;
          const value = String(raw);
          return {
            label,
            value: format ? format(value) : value,
            copyable: Boolean(copyable),
            truncateDisplay: Boolean(truncateDisplay),
            monospace: Boolean(monospace),
          };
        })
        .filter((item): item is { label: string; value: string; copyable: boolean; truncateDisplay: boolean; monospace: boolean } => item !== null);
      return { title, icon, items };
    }).filter((section) => section.items.length > 0);

    const isDark = theme.palette.mode === "dark";
    // Indigo — this app's own brand color (theme.ts primary #3A2ADA) — used as
    // the ONE consistent accent for every section icon badge, rather than
    // a different color per section.
    const accent = isDark ? "#7C6FF0" : "#3A2ADA";
    const accentTint = isDark ? "rgba(124,111,240,0.16)" : "rgba(58,42,218,0.08)";
    const borderColor = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";

    const renderSection = (section: { title: string; icon: React.ElementType; items: { label: string; value: string; copyable: boolean; truncateDisplay: boolean; monospace: boolean }[] }) => {
      const SectionIcon = section.icon;
      const rowDividerColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.045)";
      const labelColor = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.55)";
      // Two fields per row (label stacked above value) instead of one field
      // per full-width row — denser, and the divider reads as separating
      // logical rows of data rather than every single field.
      const fieldRows: typeof section.items[] = [];
      for (let i = 0; i < section.items.length; i += 2) {
        fieldRows.push(section.items.slice(i, i + 2));
      }
      return (
        <Box
          key={section.title}
          sx={{
            border: "1px solid",
            borderColor,
            borderRadius: 2,
            p: 2.5,
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: accentTint,
                color: accent,
                flexShrink: 0,
              }}
            >
              <SectionIcon sx={{ fontSize: 15 }} />
            </Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: accent, textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.75rem" }}>
              {section.title}
            </Typography>
          </Box>
          {fieldRows.map((rowItems, rowIdx) => {
            const isLastRow = rowIdx === fieldRows.length - 1;
            return (
              <Box
                key={rowIdx}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  columnGap: 3,
                  py: 1.25,
                  borderBottom: isLastRow ? "none" : "1px solid",
                  borderColor: rowDividerColor,
                }}
              >
                {rowItems.map((item) => {
                  const statusColor = STATUS_DOT_COLORS[item.value.toLowerCase()];
                  return (
                    <Box key={item.label} sx={{ minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", color: labelColor, fontSize: "0.6875rem", textTransform: "uppercase", letterSpacing: 0.5 }}
                      >
                        {item.label}
                      </Typography>
                      {/* Long values (processor names, full UUIDs, disk model
                          numbers) wrap to a second line instead of hiding
                          behind an ellipsis — full readability wins over a
                          single-line row. */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.375 }}>
                        {statusColor && (
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: statusColor, flexShrink: 0 }} />
                        )}
                        <WhiteTooltip title={item.truncateDisplay ? item.value : ""}>
                          <Typography
                            variant="body2"
                            fontWeight={item.monospace ? 700 : 600}
                            sx={{
                              wordBreak: "break-word",
                              fontFamily: item.monospace
                                ? 'ui-monospace,"SF Mono","Cascadia Mono","Roboto Mono",Consolas,monospace'
                                : undefined,
                              fontSize: "0.875rem",
                            }}
                          >
                            {item.truncateDisplay ? truncateMiddle(item.value) : item.value}
                          </Typography>
                        </WhiteTooltip>
                        {item.copyable && <CopyIconButton value={item.value} />}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      );
    };

    return (
      <Box sx={{ pb: 2 }}>
        {/* Hero card (icon/hostname/device type/severity) now lives in the
            drawer's sticky header — see computeHeroInfo — instead of being
            repeated here, since the header is visible on every tab anyway. */}
        {sections.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No asset details available
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Each section is now a full-width card (not a paired 2-column
                grid) — its fields form their own internal 2-column grid, so
                sections read as flowing spec-sheet blocks rather than tiles
                that need to line up in height against a neighbor.
                leaving a gap under a short column or cards of mismatched
                sizes sitting next to each other. */}
            {sections.map(renderSection)}
          </Box>
        )}
      </Box>
    );
  };

  // Column definitions for ports overlay
  const portsColumnDefs = [
    {
      headerName: "Port",
      field: "port",
      minWidth: 100,
      sortable: true,
      filter: true,
      valueFormatter: (params: any) => params.value === null || params.value === undefined || params.value === "" ? "-" : params.value
    },
    {
      headerName: "Protocol",
      field: "protocol",
      minWidth: 120,
      sortable: true,
      filter: true,
      valueFormatter: (params: any) => params.value === null || params.value === undefined || params.value === "" ? "-" : params.value
    },
    {
      headerName: "State",
      field: "state",
      minWidth: 100,
      sortable: true,
      filter: true,
      valueFormatter: (params: any) => params.value === null || params.value === undefined || params.value === "" ? "-" : params.value
    },
    {
      headerName: "State Reason",
      field: "state_reason",
      minWidth: 150,
      sortable: true,
      filter: true,
      hide: true,
      valueFormatter: (params: any) => params.value === null || params.value === undefined || params.value === "" ? "-" : params.value
    },
    {
      headerName: "State TTL",
      field: "state_ttl",
      minWidth: 120,
      sortable: true,
      filter: true,
      valueFormatter: (params: any) => params.value === null || params.value === undefined || params.value === "" ? "-" : params.value
    },
    {
      headerName: "Service Name",
      field: "service_name",
      minWidth: 150,
      sortable: true,
      filter: true,
      valueFormatter: (params: any) => params.value === null || params.value === undefined || params.value === "" ? "-" : params.value
    },
    {
      headerName: "Service Product",
      field: "service_product",
      minWidth: 200,
      sortable: true,
      filter: true,
      valueFormatter: (params: any) => params.value === null || params.value === undefined || params.value === "" ? "-" : params.value
    },
    {
      headerName: "Service Method",
      field: "service_method",
      minWidth: 150,
      sortable: true,
      filter: true,
      valueFormatter: (params: any) => params.value === null || params.value === undefined || params.value === "" ? "-" : params.value
    },
    {
      headerName: "Service Confidence",
      field: "service_confidence",
      minWidth: 150,
      sortable: true,
      filter: true,
      valueFormatter: (params: any) => params.value === null || params.value === undefined || params.value === "" ? "-" : params.value
    }
  ];

  // Column definitions for OS overlay
  const osColumnDefs = [
    {
      headerName: "Name",
      field: "name",
      minWidth: 250,
      sortable: true,
      filter: true,
      valueFormatter: (params: any) => params.value === null || params.value === undefined || params.value === "" ? "-" : params.value
    },
    {
      headerName: "Accuracy",
      field: "accuracy",
      minWidth: 100,
      sortable: true,
      filter: true,
      valueFormatter: (params: any) => params.value === null || params.value === undefined || params.value === "" ? "-" : params.value
    },
    {
      headerName: "Family",
      field: "family",
      minWidth: 150,
      sortable: true,
      filter: true,
      valueFormatter: (params: any) => params.value === null || params.value === undefined || params.value === "" ? "-" : params.value
    },
    {
      headerName: "Vendor",
      field: "vendor",
      minWidth: 150,
      sortable: true,
      filter: true,
      valueFormatter: (params: any) => params.value === null || params.value === undefined || params.value === "" ? "-" : params.value
    }
  ];

  // Quick filters now search the full dataset (which can be larger than the
  // current page), so whether one is active is checked directly rather than
  // inferring it from filteredCount < discoveryData.length — that
  // comparison alone only holds when the filtered pool is the current page
  // (e.g. AG-Grid's own search box).
  const activeQuickFilterCount =
    quickFilters.deviceTypes.length +
    quickFilters.vendors.length +
    quickFilters.osNames.length +
    (selectedCategoryFilter !== "all" ? 1 : 0);

  // Calculate display range for pagination info.
  const isLocalFiltered = activeQuickFilterCount > 0 || (filteredCount !== null && filteredCount < discoveryData.length);
  const startRow = totalRows === 0 ? 0 : isLocalFiltered ? (filteredCount === 0 ? 0 : 1) : (currentPage - 1) * PAGE_SIZE + 1;
  const endRow = isLocalFiltered
    ? filteredCount
    : Math.min(currentPage * PAGE_SIZE, totalRows);

  const showNoOpenPortsMessage = 
    scanStatusInfo && 
    (scanStatusInfo.status === "Success" || scanStatusInfo.status === "Completed") && 
    Number(scanStatusInfo.assetsAssessed ?? 0) === 0 && 
    Number(scanStatusInfo.assetsDiscovered ?? 0) === 0;

  const noRowsMessage = showNoOpenPortsMessage
    ? "No open ports are available on the reachable IPs."
    : undefined;

  // The stat-card category: a device-type bucket, or "ai" for assets with at least one AI agent.
  const inSelectedCategory = (deviceType: string, aiCount: number, aiTools: string[]) =>
    selectedCategoryFilter === "all" ||
    (selectedCategoryFilter === "ai"
      ? aiCount > 0 && (!aiToolFilter || aiTools.includes(aiToolFilter))
      : categorizeDeviceType(deviceType) === selectedCategoryFilter);

  // Quick filters (Device Type / Vendor / OS Name / Stat Card Category) —
  // filters across the FULL dataset (allAssetRows, the same all-pages fetch
  // used for the stat cards), not just discoveryData (the current
  // server-paginated page).
  const filteredDiscoveryData = useMemo(() => {
    const hasQuickFilters =
      deferredQuickFilters.deviceTypes.length > 0 ||
      deferredQuickFilters.vendors.length > 0 ||
      deferredQuickFilters.osNames.length > 0;
    const hasCategoryFilter = selectedCategoryFilter !== "all";

    if (!hasQuickFilters && !hasCategoryFilter) {
      return discoveryData;
    }
    return allAssetRows.filter((row) => {
      const dtLower = String(row.deviceType || "").toLowerCase();

      // Card Category Filter
      const categoryMatch = inSelectedCategory(dtLower, Number(row.ai_agents_count) || 0, Array.isArray(row.ai_tools) ? row.ai_tools : []);

      // Dropdown Quick Filters
      const deviceTypeMatch = isFacetActive(deferredQuickFilters.deviceTypes, dtLower || "unknown");
      const vendorMatch = isFacetActive(deferredQuickFilters.vendors, String(row.vendor || "").trim());
      const osNameMatch = isFacetActive(deferredQuickFilters.osNames, String(row.osName || "").trim() || "Unknown");

      return categoryMatch && deviceTypeMatch && vendorMatch && osNameMatch;
    });
  }, [discoveryData, allAssetRows, deferredQuickFilters, selectedCategoryFilter, aiToolFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const isFilterStale = quickFilters !== deferredQuickFilters;

  // Available options: populated from the full dataset (all-pages).
  // We filter these against the selected category card (metric card) so the dropdowns
  // only show relevant options for the active tab (e.g. only showing "Router" when "Network Devices" is active).
  // We intentionally do NOT cross-filter them against other quick filters to avoid a trap 
  // where a bad combination hides options completely.
  const availableDeviceTypes = useMemo(() => {
    const rowsToConsider = assetFacetRows.filter(r => inSelectedCategory(r.deviceType, r.aiCount, r.aiTools));
    return Array.from(new Set(rowsToConsider.map((r) => r.deviceType))).sort();
  }, [assetFacetRows, selectedCategoryFilter, aiToolFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableVendors = useMemo(() => {
    const rowsToConsider = assetFacetRows.filter(r => inSelectedCategory(r.deviceType, r.aiCount, r.aiTools));
    return Array.from(new Set(rowsToConsider.map((r) => r.vendor).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [assetFacetRows, selectedCategoryFilter, aiToolFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableOsNames = useMemo(() => {
    const rowsToConsider = assetFacetRows.filter(r => inSelectedCategory(r.deviceType, r.aiCount, r.aiTools));
    return Array.from(new Set(rowsToConsider.map((r) => r.osName).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [assetFacetRows, selectedCategoryFilter, aiToolFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearAllFilters = () => {
    setQuickFilters({ deviceTypes: [], vendors: [], osNames: [] });
    setSelectedCategoryFilter("all");
    setAiToolFilter(null);
  };

  const isAssetInventoryPage = !fitDrawer && !discoveryScanId;

  // Small window of page-number buttons centered on the current page.
  const pageNumbers: number[] = isLocalFiltered || totalPages <= 1
    ? []
    : (() => {
        const nums: number[] = [];
        const start = Math.max(1, currentPage - 1);
        const end = Math.min(totalPages, start + 2);
        for (let p = start; p <= end; p++) nums.push(p);
        return nums;
      })();

  // When a quick filter is active, recompute the stat cards from the full
  // (all-pages) facet dataset filtered the same way — not from
  // filteredDiscoveryData, which is only the current page's rows and would
  // undercount. Falls back to the unfiltered global totals otherwise.
  const filteredStatCounts = useMemo(() => {
    if (quickFilters.deviceTypes.length === 0 && quickFilters.vendors.length === 0 && quickFilters.osNames.length === 0) return null;
    const relevant = assetFacetRows.filter(
      (r) =>
        isFacetActive(quickFilters.deviceTypes, r.deviceType) &&
        isFacetActive(quickFilters.vendors, r.vendor) &&
        isFacetActive(quickFilters.osNames, r.osName)
    );
    let servers = 0;
    let workstations = 0;
    let networkDevices = 0;
    let iotDevices = 0;
    let aiAgents = 0;
    for (const r of relevant) {
      aiAgents += r.aiCount;
      const bucket = categorizeDeviceType(r.deviceType);
      if (bucket === "servers") servers += 1;
      else if (bucket === "workstations") workstations += 1;
      else if (bucket === "networkDevices") networkDevices += 1;
      else if (bucket === "iotDevices") iotDevices += 1;
    }
    return { total: relevant.length, servers, workstations, networkDevices, iotDevices, aiAgents };
  }, [assetFacetRows, quickFilters]);

  const displayedStats = filteredStatCounts ?? assetStats;

  // Each AI agent and how many assets it's on, over the same assets the card counts.
  const aiAgentList = useMemo(() => {
    const byTool = new Map<string, number>();
    let assetsWithAi = 0;
    for (const r of assetFacetRows) {
      if (
        !isFacetActive(quickFilters.deviceTypes, r.deviceType) ||
        !isFacetActive(quickFilters.vendors, r.vendor) ||
        !isFacetActive(quickFilters.osNames, r.osName)
      ) continue;
      if (r.aiCount > 0) assetsWithAi += 1;
      new Set(r.aiTools).forEach((t) => byTool.set(t, (byTool.get(t) ?? 0) + 1));
    }
    const agents = [...byTool.entries()]
      .map(([name, assets]) => ({ name, assets }))
      .sort((a, b) => b.assets - a.assets || a.name.localeCompare(b.name));
    return { agents, assetsWithAi };
  }, [assetFacetRows, quickFilters]);

  const showAiAgentTool = (tool: string | null) => {
    setSelectedCategoryFilter("ai");
    setAiToolFilter(tool);
    setAiPanelOpen(false);
  };

  const statCards: Array<{
    id: "all" | "servers" | "workstations" | "networkDevices" | "iotDevices" | "ai";
    icon: React.ElementType;
    label: string;
    value: string;
    color: string;
  }> = displayedStats
    ? [
        { id: "all", icon: DesktopWindowsIcon, label: "Total Assets", value: String(displayedStats.total), color: "#3B82F6" },
        { id: "servers", icon: DnsIcon, label: "Servers", value: String(displayedStats.servers), color: "#6366F1" },
        { id: "workstations", icon: ComputerIcon, label: "Workstations", value: String(displayedStats.workstations), color: "#3B82F6" },
        { id: "networkDevices", icon: RouterIcon, label: "Network Devices", value: String(displayedStats.networkDevices), color: "#8B5CF6" },
        { id: "iotDevices", icon: SensorsIcon, label: "IoT Devices", value: String(displayedStats.iotDevices), color: "#EC4899" },
        { id: "ai", icon: SmartToyOutlinedIcon, label: "AI Agents", value: String(displayedStats.aiAgents), color: "#10B981" },
      ]
    : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      {isAssetInventoryPage && (
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          {statCards.map((card) => {
            const isSelected = selectedCategoryFilter === card.id;
            return (
              <Box
                key={card.id}
                onClick={() => {
                  if (selectedCategoryFilter === card.id) {
                    setSelectedCategoryFilter("all");
                    setAiToolFilter(null);
                  } else if (card.id === "ai") {
                    // The AI Agents card opens the list of agents; picking one narrows the table.
                    setAiPanelOpen(true);
                  } else {
                    setSelectedCategoryFilter(card.id);
                    setAiToolFilter(null);
                  }
                }}
                sx={{
                  flex: "1 1 190px",
                  minWidth: 180,
                  px: 2.75,
                  py: 2.25,
                  borderRadius: 2.5,
                  border: "2px solid",
                  borderColor: isSelected ? card.color : "divider",
                  bgcolor: isSelected
                    ? (theme) => (theme.palette.mode === "dark" ? `${card.color}22` : `${card.color}0D`)
                    : "background.paper",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  boxShadow: isSelected
                    ? `0 4px 14px ${card.color}33`
                    : "0 1px 3px rgba(0,0,0,0.04)",
                  transition: "all 0.15s ease-in-out",
                  userSelect: "none",
                  "&:hover": {
                    boxShadow: `0 4px 14px ${card.color}25`,
                    borderColor: card.color,
                    transform: "translateY(-1px)",
                  },
                }}
              >
                {/* Line 1: Logo beside Number */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <card.icon sx={{ fontSize: 26, color: card.color, flexShrink: 0 }} />
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      fontSize: "1.5rem",
                      lineHeight: 1,
                      color: "text.primary",
                    }}
                  >
                    {card.value}
                  </Typography>
                </Box>

                {/* Line 2: Name below */}
                <Typography
                  variant="body2"
                  fontWeight={isSelected ? 700 : 500}
                  color={isSelected ? "text.primary" : "text.secondary"}
                  sx={{
                    fontSize: "0.925rem",
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {card.id === "ai" && isSelected && aiToolFilter ? `AI Agents · ${aiToolFilter}` : card.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      <Drawer anchor="right" open={aiPanelOpen} onClose={() => setAiPanelOpen(false)} PaperProps={{ sx: { width: { xs: "100%", sm: 400 } } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", p: 2.5, pb: 1.5 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>AI Agents</Typography>
            <Typography variant="body2" color="text.secondary">
              {aiAgentList.agents.length
                ? `${aiAgentList.agents.length} agent${aiAgentList.agents.length === 1 ? "" : "s"} across ${aiAgentList.assetsWithAi} asset${aiAgentList.assetsWithAi === 1 ? "" : "s"}`
                : "No AI agents found"}
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={() => setAiPanelOpen(false)} size="small"><CloseIcon /></IconButton>
        </Box>
        <Divider />
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {aiAgentList.agents.length > 0 && (
            <Box
              onClick={() => showAiAgentTool(null)}
              sx={{ px: 2.5, py: 1.5, cursor: "pointer", display: "flex", justifyContent: "space-between", color: "primary.main", fontWeight: 600, fontSize: "0.9rem", "&:hover": { bgcolor: "action.hover" } }}
            >
              <span>Show all assets with AI agents</span>
              <span>{aiAgentList.assetsWithAi}</span>
            </Box>
          )}
          {aiAgentList.agents.map((agent) => (
            <Box
              key={agent.name}
              onClick={() => showAiAgentTool(agent.name)}
              sx={{ px: 2.5, py: 1.5, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, borderTop: "1px solid", borderColor: "divider", "&:hover": { bgcolor: "action.hover" } }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{agent.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                {agent.assets} asset{agent.assets === 1 ? "" : "s"}
              </Typography>
            </Box>
          ))}
        </Box>
      </Drawer>

      <Card
        variant="outlined"
        sx={{
          height: "100%",
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: "hidden"
        }}
      >
        {isAssetInventoryPage && (
          <Popover
            open={Boolean(filtersAnchorEl)}
            anchorEl={filtersAnchorEl}
            onClose={() => {
              setFiltersAnchorEl(null);
              setFilterSearchQuery("");
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
                border: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            <Box sx={{ width: 270, p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: "13px", mb: 1 }}>
                Filters
              </Typography>

              {/* Segmented Tab Switcher for Category */}
              <Box
                sx={{
                  display: "flex",
                  bgcolor: "action.hover",
                  p: 0.35,
                  borderRadius: 1.25,
                  mb: 1,
                  gap: 0.35,
                }}
              >
                {[
                  { key: "deviceTypes", label: "Device Type" },
                  { key: "vendors", label: "Vendor" },
                  { key: "osNames", label: "OS" },
                ].map((tab) => {
                  const isActive = activeFilterTab === tab.key;
                  return (
                    <Box
                      key={tab.key}
                      onClick={() => {
                        setActiveFilterTab(tab.key as any);
                        setFilterSearchQuery("");
                      }}
                      sx={{
                        flex: 1,
                        py: 0.4,
                        px: 0.5,
                        borderRadius: 1,
                        cursor: "pointer",
                        fontSize: "11.5px",
                        fontWeight: isActive ? 600 : 500,
                        textAlign: "center",
                        bgcolor: isActive ? "primary.main" : "transparent",
                        color: isActive ? "primary.contrastText" : "text.secondary",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          color: isActive ? "primary.contrastText" : "text.primary",
                        },
                      }}
                    >
                      <span>{tab.label}</span>
                    </Box>
                  );
                })}
              </Box>

              {/* Search input */}
              <TextField
                size="small"
                placeholder="Search..."
                value={filterSearchQuery}
                onChange={(e) => setFilterSearchQuery(e.target.value)}
                fullWidth
                sx={{
                  mb: 1,
                  "& .MuiOutlinedInput-root": {
                    height: 30,
                    fontSize: "12px",
                    borderRadius: 1,
                  },
                  "& .MuiOutlinedInput-input": {
                    py: 0.3,
                    px: 1,
                  },
                }}
              />

              {/* Options list */}
              <Box
                sx={{
                  maxHeight: 175,
                  overflowY: "auto",
                  pr: 0.3,
                  scrollbarWidth: "thin",
                  scrollbarColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.2) transparent"
                      : "rgba(0, 0, 0, 0.2) transparent",
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.2)",
                    borderRadius: "4px",
                    "&:hover": {
                      background: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.35)"
                          : "rgba(0, 0, 0, 0.35)",
                    },
                  },
                  "&::-webkit-scrollbar-button": {
                    display: "none",
                  },
                }}
              >
                {(() => {
                  const currentSelected =
                    activeFilterTab === "deviceTypes"
                      ? tempQuickFilters.deviceTypes
                      : activeFilterTab === "vendors"
                      ? tempQuickFilters.vendors
                      : tempQuickFilters.osNames;

                  const currentOptions =
                    activeFilterTab === "deviceTypes"
                      ? availableDeviceTypes
                      : activeFilterTab === "vendors"
                      ? availableVendors
                      : availableOsNames;

                  const isAllSelected =
                    currentSelected.length === 0 ||
                    (!currentSelected.includes("__NONE__") &&
                      currentOptions.length > 0 &&
                      currentOptions.every((opt) => currentSelected.includes(opt)));

                  const filteredOptions = currentOptions.filter((opt) =>
                    opt.toLowerCase().includes(filterSearchQuery.toLowerCase())
                  );

                  return (
                    <>
                      <Box
                        onClick={() => {
                          if (isAllSelected) {
                            setTempQuickFilters((prev) => ({
                              ...prev,
                              [activeFilterTab]: ["__NONE__"],
                            }));
                          } else {
                            setTempQuickFilters((prev) => ({
                              ...prev,
                              [activeFilterTab]: [],
                            }));
                          }
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 0.75,
                          py: 0.35,
                          borderRadius: 1,
                          cursor: "pointer",
                          mb: 0.15,
                          bgcolor: isAllSelected ? "action.selected" : "transparent",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={isAllSelected}
                          checkedIcon={
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "3.5px",
                                bgcolor: "primary.main",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <CheckIcon sx={{ fontSize: 11, color: "#ffffff" }} />
                            </Box>
                          }
                          icon={
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "3.5px",
                                border: "1.5px solid",
                                borderColor: "text.secondary",
                                bgcolor: "transparent",
                              }}
                            />
                          }
                          sx={{ p: 0.25, mr: 0.75 }}
                        />
                        <Typography variant="body2" sx={{ fontSize: "12px", fontWeight: isAllSelected ? 600 : 400 }}>
                          All
                        </Typography>
                      </Box>

                      {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt) => {
                          const isSelected = isAllSelected || currentSelected.includes(opt);
                          const formattedLabel =
                            activeFilterTab === "deviceTypes"
                              ? opt.charAt(0).toUpperCase() + opt.slice(1)
                              : opt;

                          return (
                            <Box
                              key={opt}
                              onClick={() => {
                                setTempQuickFilters((prev) => {
                                  const list = prev[activeFilterTab];
                                  let updated: string[];

                                  if (isAllSelected) {
                                    updated = currentOptions.filter((item) => item !== opt);
                                    if (updated.length === 0) {
                                      updated = ["__NONE__"];
                                    }
                                  } else if (list.includes("__NONE__")) {
                                    updated = [opt];
                                    if (updated.length >= currentOptions.length && currentOptions.length > 0) {
                                      updated = [];
                                    }
                                  } else {
                                    const exists = list.includes(opt);
                                    if (exists) {
                                      updated = list.filter((item) => item !== opt);
                                      if (updated.length === 0) {
                                        updated = ["__NONE__"];
                                      }
                                    } else {
                                      updated = [...list, opt];
                                      if (updated.length >= currentOptions.length && currentOptions.length > 0) {
                                        updated = [];
                                      }
                                    }
                                  }

                                  return { ...prev, [activeFilterTab]: updated };
                                });
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                px: 0.75,
                                py: 0.35,
                                borderRadius: 1,
                                cursor: "pointer",
                                mb: 0.15,
                                bgcolor: isSelected ? "action.selected" : "transparent",
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={isSelected}
                                checkedIcon={
                                  <Box
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      borderRadius: "3.5px",
                                      bgcolor: "primary.main",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <CheckIcon sx={{ fontSize: 11, color: "#ffffff" }} />
                                  </Box>
                                }
                                icon={
                                  <Box
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      borderRadius: "3.5px",
                                      border: "1.5px solid",
                                      borderColor: "text.secondary",
                                      bgcolor: "transparent",
                                    }}
                                  />
                                }
                                sx={{ p: 0.25, mr: 0.75 }}
                              />
                              <Typography variant="body2" sx={{ fontSize: "12px", color: "text.primary" }}>
                                {formattedLabel}
                              </Typography>
                            </Box>
                          );
                        })
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "12px", py: 1, textAlign: "center" }}
                        >
                          No options found
                        </Typography>
                      )}
                    </>
                  );
                })()}
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                  pt: 1,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setTempQuickFilters({ deviceTypes: [], vendors: [], osNames: [] });
                    clearAllFilters();
                    setFilterSearchQuery("");
                  }}
                  sx={{
                    textTransform: "none",
                    fontSize: "11.5px",
                    px: 1.25,
                    py: 0.25,
                    borderColor: "divider",
                    color: "text.primary",
                    "&:hover": { borderColor: "text.secondary", bgcolor: "action.hover" },
                  }}
                >
                  Clear
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setQuickFilters(tempQuickFilters);
                    setFiltersAnchorEl(null);
                    setFilterSearchQuery("");
                  }}
                  sx={{
                    textTransform: "none",
                    fontSize: "11.5px",
                    px: 1.75,
                    py: 0.25,
                    boxShadow: "none",
                    bgcolor: "primary.main",
                    "&:hover": { bgcolor: "primary.dark", boxShadow: "none" },
                  }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Popover>
        )}

        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", "& .ag-theme-material": { opacity: isFilterStale ? 0.6 : 1, transition: "opacity 0.15s ease" } }}>
        {isAssetInventoryPage && viewMode === "grid" ? (
          <Box sx={{ flex: 1, overflow: "auto", p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 1 }}>
              <Button
                size="small"
                variant={Boolean(filtersAnchorEl) || activeQuickFilterCount > 0 ? "contained" : "outlined"}
                startIcon={<FilterListIcon />}
                endIcon={Boolean(filtersAnchorEl) ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                onClick={(e) => {
                  setFiltersAnchorEl(e.currentTarget);
                  setTempQuickFilters(quickFilters);
                }}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  ...(Boolean(filtersAnchorEl) || activeQuickFilterCount > 0
                    ? {
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        borderColor: "primary.main",
                        "& .MuiSvgIcon-root": { color: "primary.contrastText" },
                        "&:hover": {
                          bgcolor: "primary.dark",
                          borderColor: "primary.dark",
                        },
                      }
                    : {
                        color: "text.primary",
                        borderColor: "divider",
                        "& .MuiSvgIcon-root": { color: "text.primary" },
                        "&:hover": {
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          borderColor: "primary.main",
                          "& .MuiSvgIcon-root": { color: "primary.contrastText" },
                        },
                      }),
                }}
              >
                Filters
              </Button>
              <Box sx={{ display: "flex", border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 0.25, gap: 0.25 }}>
                <IconButton
                  size="small"
                  onClick={() => setViewMode("list")}
                  sx={{
                    borderRadius: 1,
                    color: viewMode === "list" ? "primary.main" : "text.secondary",
                    bgcolor: viewMode === "list" ? "action.selected" : "transparent",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                  title="Table View"
                >
                  <ViewListIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setViewMode("grid")}
                  sx={{
                    borderRadius: 1,
                    color: viewMode === "grid" ? "primary.main" : "text.secondary",
                    bgcolor: viewMode === "grid" ? "action.selected" : "transparent",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                  title="Grid View"
                >
                  <ViewModuleIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 2, opacity: isFilterStale ? 0.6 : 1, transition: "opacity 0.15s ease" }}>
              {filteredDiscoveryData.map((row) => {
                const { icon: DeviceTypeIcon, color } = getDeviceTypeChipStyle(row.deviceType);
                return (
                  <Box
                    key={row.id || row.ip}
                    onClick={() => {
                      setSelectedRowData(row);
                      setDetailsDrawerOpen(true);
                      fetchIpDetails(row.ip, row.module, row.tool);
                    }}
                    sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", cursor: "pointer", display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <DeviceTypeIcon sx={{ fontSize: 18, color }} />
                      <Typography variant="subtitle2" fontWeight={700} noWrap>
                        {row.hostname || row.ip || "-"}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {row.ip}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                        {row.deviceType || "Unknown"}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : (
        <AgGridTable
          columnDefs={columnDefs}
          data={filteredDiscoveryData}
          gridRef={gridRef}
          showSearch
          searchPlaceholder="Search..."
          isLoading={isLoading}
          isRowSelectable
          pagination={false}
          gridOptions={memoizedGridOptions}
          height={"100%"}
          noRowsMessage={noRowsMessage}
          showAppsIcon={isAssetInventoryPage}
          headerActions={
            isAssetInventoryPage ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  size="small"
                  variant={Boolean(filtersAnchorEl) || activeQuickFilterCount > 0 ? "contained" : "outlined"}
                  startIcon={<FilterListIcon />}
                  endIcon={Boolean(filtersAnchorEl) ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  onClick={(e) => {
                    setFiltersAnchorEl(e.currentTarget);
                    setTempQuickFilters(quickFilters);
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 1.5,
                    ...(Boolean(filtersAnchorEl) || activeQuickFilterCount > 0
                      ? {
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          borderColor: "primary.main",
                          "& .MuiSvgIcon-root": { color: "primary.contrastText" },
                          "&:hover": {
                            bgcolor: "primary.dark",
                            borderColor: "primary.dark",
                          },
                        }
                      : {
                          color: "text.primary",
                          borderColor: "divider",
                          "& .MuiSvgIcon-root": { color: "text.primary" },
                          "&:hover": {
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            borderColor: "primary.main",
                            "& .MuiSvgIcon-root": { color: "primary.contrastText" },
                          },
                        }),
                  }}
                >
                  Filters
                </Button>
                <Box sx={{ display: "flex", border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 0.25, gap: 0.25 }}>
                  <IconButton
                    size="small"
                    onClick={() => setViewMode("list")}
                    sx={{
                      borderRadius: 1,
                      color: viewMode === "list" ? "primary.main" : "text.secondary",
                      bgcolor: viewMode === "list" ? "action.selected" : "transparent",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                    title="Table View"
                  >
                    <ViewListIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setViewMode("grid")}
                    sx={{
                      borderRadius: 1,
                      color: viewMode === "grid" ? "primary.main" : "text.secondary",
                      bgcolor: viewMode === "grid" ? "action.selected" : "transparent",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                    title="Grid View"
                  >
                    <ViewModuleIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ) : undefined
          }
          onSearchSubmit={(searchText) => {
            // Trigger server search whenever Enter is pressed
            console.log('🔍 Discovery: Triggering server search for:', searchText);
            fetchSecurityScanData(1, searchText);
          }}
          onFilteredCountChange={(count) => {
            setFilteredCount(count);
          }}
          onSearchClear={() => {
            // When search is cleared, reset and fetch original data
            console.log('🔄 Discovery: Search cleared, fetching original data');
            fetchSecurityScanData(1, "");
          }}
        />
        )}
        </Box>

        {/* Custom Server-Side Pagination Controls */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 2,
            py: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            backgroundColor: theme.palette.mode === 'dark' ? '#393E4B' : '#FFFFFF',
            color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
            minHeight: 44,
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
            <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.secondary', fontWeight: 500 }}>
              Page Size: {PAGE_SIZE}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.secondary', fontWeight: 500 }}>
              Showing {startRow} to {endRow} of {isLocalFiltered ? filteredCount : totalRows} assets
            </Typography>
          </Box>

          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={handleFirstPage}
              disabled={!hasPreviousPage || isLoading || isLocalFiltered}
              sx={{
                p: 0.5,
                color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
                '&:disabled': { opacity: 0.3, color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : undefined },
              }}
            >
              <FirstPageIcon fontSize="small" />
            </IconButton>

            <IconButton
              size="small"
              onClick={handlePreviousPage}
              disabled={!hasPreviousPage || isLoading || isLocalFiltered}
              sx={{
                p: 0.5,
                color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
                '&:disabled': { opacity: 0.3, color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : undefined },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>

            {pageNumbers.length > 0 ? (
              pageNumbers.map((p) => (
                <IconButton
                  key={p}
                  size="small"
                  onClick={() => fetchSecurityScanData(p, searchKeyword)}
                  disabled={isLoading}
                  sx={{
                    p: 0.5,
                    minWidth: 28,
                    borderRadius: 1,
                    bgcolor: p === currentPage ? 'primary.main' : 'transparent',
                    color: p === currentPage ? '#ffffff' : theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
                    '&:hover': { bgcolor: p === currentPage ? 'primary.main' : 'action.hover' },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>{p}</Typography>
                </IconButton>
              ))
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 500, mx: 1, color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary' }}>
                Page {isLocalFiltered ? (filteredCount === 0 ? 0 : 1) : currentPage} of {isLocalFiltered ? (filteredCount === 0 ? 0 : 1) : totalPages}
              </Typography>
            )}

            <IconButton
              size="small"
              onClick={handleNextPage}
              disabled={!hasNextPage || isLoading || isLocalFiltered}
              sx={{
                p: 0.5,
                color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
                '&:disabled': { opacity: 0.3, color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : undefined },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>

            <IconButton
              size="small"
              onClick={handleLastPage}
              disabled={!hasNextPage || isLoading || isLocalFiltered}
              sx={{
                p: 0.5,
                color: theme.palette.mode === 'dark' ? '#ffffff' : 'text.primary',
                '&:disabled': { opacity: 0.3, color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : undefined },
              }}
            >
              <LastPageIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Card>

      {/* Ports Overlay */}
      <Drawer
        anchor="right"
        open={portsOverlayOpen}
        onClose={() => setPortsOverlayOpen(false)}
        PaperProps={{
          sx: {
            width: "50%",
            overflow: "hidden",
            bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF",
          },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            zIndex: 1200,
            borderBottom: "1px solid",
            borderColor: "divider",
            p: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Ports
            </Typography>
            <IconButton onClick={() => setPortsOverlayOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ p: fitDrawer ? 0 : 2, height: fitDrawer ? '100%' : "calc(100vh - 80px)" }}>
          <AgGridTable
            columnDefs={portsColumnDefs}
            data={selectedRowPorts}
            showSearch
            height={fitDrawer ? '100%' : "calc(100vh - 160px)"}
            pagination={true}
          />
        </Box>
      </Drawer>

      {/* OS Overlay */}
      <Drawer
        anchor="right"
        open={osOverlayOpen}
        onClose={() => setOsOverlayOpen(false)}
        PaperProps={{
          sx: {
            width: "50%",
            overflow: "hidden",
            bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF",
          },
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            bgcolor: "background.paper",
            zIndex: 1200,
            borderBottom: "1px solid",
            borderColor: "divider",
            p: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              OS
            </Typography>
            <IconButton onClick={() => setOsOverlayOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ p: fitDrawer ? 0 : 2, height: fitDrawer ? '100%' : "calc(100vh - 80px)" }}>
          <AgGridTable
            columnDefs={osColumnDefs}
            data={selectedRowOS}
            showSearch
            height={fitDrawer ? '100%' : "calc(100vh - 160px)"}
            pagination={true}
          />
        </Box>
      </Drawer>

      {/* Details Drawer */}
      <Drawer
        anchor="right"
        open={detailsDrawerOpen}
        onClose={() => setDetailsDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: "50%",
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF",
          },
        }}
      >
        {(() => {
          const deviceTypeLower = String(
            selectedRowData?.deviceType || buildFieldLookup(ipDetailsData || selectedRowData).device_type || ""
          ).toLowerCase();
          const isExcludedDistro = isExcludedLinuxDistro(selectedRowData, ipDetailsData);
          // Hosts the endpoint agent can report on. "endpoint" is what the classifier calls agent-managed
          // Windows/macOS machines; a row with ai_agents_count !== null already has an endpoint snapshot.
          const showSoftwareAiTabs =
            /workstation|server|desktop|laptop|endpoint/.test(deviceTypeLower) ||
            (selectedRowData?.ai_agents_count !== null && selectedRowData?.ai_agents_count !== undefined);

          const tabs = [
            { id: "assetInfo", label: "Asset Info" },
            ...(showSoftwareAiTabs ? [{ id: "software", label: "Software" }, { id: "aiAgent", label: "AI Agent" }] : []),
            { id: "fieldValue", label: "Field Value" },
          ];
          const activeTabId = tabs.some((t) => t.id === detailsTab) ? detailsTab : "assetInfo";

          return (
            <>
              {/* Fixed Top Header + Tabs */}
              <Box
                sx={{
                  flexShrink: 0,
                  bgcolor: "background.paper",
                  zIndex: 1200,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  px: 2,
                  pt: 2,
                  pb: 0,
                }}
              >
                {(() => {
                  if (!(ipDetailsData || selectedRowData)) {
                    return (
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                          Discovery Details
                        </Typography>
                        <IconButton onClick={() => setDetailsDrawerOpen(false)}>
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    );
                  }
                  const isDark = theme.palette.mode === "dark";
                  const accent = isDark ? "#7C6FF0" : "#3A2ADA";
                  const accentTint = isDark ? "rgba(124,111,240,0.16)" : "rgba(58,42,218,0.08)";
                  const { hostnameDisplay, HeroIcon, deviceType, critValue, critColor } = computeHeroInfo(
                    ipDetailsData || selectedRowData,
                    selectedRowData
                  );
                  return (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 1.5,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: accentTint,
                            color: accent,
                            flexShrink: 0,
                          }}
                        >
                          <HeroIcon sx={{ fontSize: 22 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                            <Typography variant="h6" fontWeight={700} noWrap>
                              {hostnameDisplay}
                            </Typography>
                            {critValue && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  fontSize: "0.625rem",
                                  fontWeight: 700,
                                  letterSpacing: 0.3,
                                  textTransform: "uppercase",
                                  color: critColor,
                                  bgcolor: `${critColor}18`,
                                  border: "1px solid",
                                  borderColor: `${critColor}55`,
                                  borderRadius: 999,
                                  px: 0.75,
                                  py: 0.125,
                                  flexShrink: 0,
                                }}
                              >
                                <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: critColor }} />
                                {critValue}
                              </Box>
                            )}
                          </Box>
                          {deviceType && (
                            <Chip
                              size="small"
                              label={deviceType}
                              variant="outlined"
                              sx={{ mt: 0.25, textTransform: "capitalize", borderRadius: "4px" }}
                            />
                          )}
                        </Box>
                      </Box>
                      <IconButton onClick={() => setDetailsDrawerOpen(false)} sx={{ flexShrink: 0 }}>
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  );
                })()}

                {(ipDetailsData || selectedRowData) && (
                  <Tabs value={activeTabId} onChange={(_, v) => setDetailsTab(v)}>
                    {tabs.map((t) => (
                      <Tab key={t.id} value={t.id} label={t.label} />
                    ))}
                  </Tabs>
                )}
              </Box>

              {/* Scrollable Tab Content Area */}
              <Box className="scroll" sx={{ flex: 1, overflowY: "auto", p: fitDrawer ? 0 : 2 }}>
                {isLoadingIpDetails ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <SkeletonLoader
                      count={Math.floor(window.innerHeight / 40)}
                      styles={{ height: 30, width: "100%" }}
                    />
                  </Box>
                ) : (ipDetailsData || selectedRowData) ? (
                  <Box sx={{ height: "100%" }}>
                    {activeTabId === "assetInfo" && (
                      renderAssetInfo(ipDetailsData || selectedRowData, selectedRowData)
                    )}

                    {activeTabId === "software" && (
                      <Box sx={{ height: '100%', width: '100%' }}>
                        <Software fitDrawer={true} ip={ip} />
                      </Box>
                    )}

                    {activeTabId === "aiAgent" && (
                      <Box sx={{ height: '100%', width: '100%' }}>
                        <AIAgentTab ip={ip} />
                      </Box>
                    )}

                    {activeTabId === "fieldValue" && renderFieldTable(ipDetailsData || selectedRowData)}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px' }}>
                    <Typography variant="body1" color="text.secondary">
                      No data available
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          );
        })()}
      </Drawer>

      {/* Rescan Popup */}
      <DiscoveryRescanPopup
        open={rescanPopupOpen}
        onClose={() => setRescanPopupOpen(false)}
        rowData={rescanRowData}
        defaultScanType="onprem-discovery"
        disableScanType={true}
      />

      {/* Asset Criticality Popover */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        anchorReference="anchorPosition"
        anchorPosition={popoverPosition || { top: 0, left: 0 }}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        PaperProps={{
          sx: { p: 0, boxShadow: 3, borderRadius: 2 },
        }}
        disableRestoreFocus
      >
        {popoverContent}
      </Popover>
    </div>
  );
};

export default Discovery;